@layer reset, base, tokens, recipes, utilities;

@layer reset{
  html,:host {
    --font-fallback: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
    line-height: 1.5;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -moz-tab-size: 4;
    tab-size: 4;
    font-family: var(--global-font-body, var(--font-fallback));
    -webkit-tap-highlight-color: transparent;
}

  *,::before,::after,::backdrop,::file-selector-button {
    margin: 0px;
    padding: 0px;
    border-width: 0px;
    border-style: solid;
    border-color: var(--global-color-border, currentcolor);
    box-sizing: border-box;
}

  hr {
    color: inherit;
    height: 0px;
    border-top-width: 1px;
}

  body {
    line-height: inherit;
    height: 100%;
}

  img {
    border-style: none;
}

  img,svg,video,canvas,audio,iframe,embed,object {
    display: block;
    vertical-align: middle;
}

  img,video {
    max-width: 100%;
    height: auto;
}

  h1,h2,h3,h4,h5,h6 {
    text-wrap: balance;
    font-size: inherit;
    font-weight: inherit;
}

  p,h1,h2,h3,h4,h5,h6 {
    overflow-wrap: break-word;
}

  ol,ul,menu {
    list-style: none;
}

  button,input:where([type='button'], [type='reset'], [type='submit']),::file-selector-button {
    appearance: button;
    -webkit-appearance: button;
}

  button,input,optgroup,select,textarea,::file-selector-button {
    font: inherit;
    background: var(--colors-transparent);
    font-feature-settings: inherit;
    font-variation-settings: inherit;
    letter-spacing: inherit;
    color: inherit;
}

  ::placeholder {
    --placeholder-fallback: rgba(0, 0, 0, 0.5);
    opacity: 1;
    color: var(--global-color-placeholder, var(--placeholder-fallback));
}

  @supports (not (-webkit-appearance: -apple-pay-button)) or (contain-intrinsic-size: 1px) {
    ::placeholder {
      --placeholder-fallback: color-mix(in oklab, currentcolor 50%, transparent);
}
}

  ::selection {
    background-color: var(--global-color-selection, rgba(0, 115, 255, 0.3));
}

  textarea {
    resize: vertical;
}

  table {
    border-color: inherit;
    text-indent: 0px;
    border-collapse: collapse;
}

  summary {
    display: list-item;
}

  small {
    font-size: 80%;
}

  sub,sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
}

  sub {
    bottom: -0.25em;
}

  sup {
    top: -0.5em;
}

  dialog {
    padding: 0px;
}

  a {
    text-decoration: inherit;
    color: inherit;
}

  abbr:where([title]) {
    text-decoration: underline dotted;
}

  b,strong {
    font-weight: bolder;
}

  code,kbd,samp,pre {
    --font-mono-fallback: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New';
    font-family: var(--global-font-mono, var(--font-mono-fallback));
    font-size: 1em;
    font-feature-settings: normal;
    font-variation-settings: normal;
}

  progress {
    vertical-align: baseline;
}

  ::-webkit-search-decoration,::-webkit-search-cancel-button {
    -webkit-appearance: none;
}

  ::-webkit-inner-spin-button,::-webkit-outer-spin-button {
    height: auto;
}

  :-moz-ui-invalid {
    box-shadow: none;
}

  :-moz-focusring {
    outline: auto;
}

  [hidden]:where(:not([hidden='until-found'])) {
    display: none !important;
}
}

@layer base{
  :root {
    --made-with-panda: '🐼';
}

  @supports selector(:has(.dashboard-container)) {
    #workspace-content:has(.dashboard-container) {
      padding-bottom: var(--spacing-0);
      height: 100%;
}
}

  @supports selector(:has(.key-topics-container)) {
    #workspace-content:has(.key-topics-container) {
      padding-left: 32px;
      padding-right: 16px;
      height: 100%;
}
}

  *,::before,::after,::backdrop {
    --blur: /*-*/ /*-*/;
    --brightness: /*-*/ /*-*/;
    --contrast: /*-*/ /*-*/;
    --grayscale: /*-*/ /*-*/;
    --hue-rotate: /*-*/ /*-*/;
    --invert: /*-*/ /*-*/;
    --saturate: /*-*/ /*-*/;
    --sepia: /*-*/ /*-*/;
    --drop-shadow: /*-*/ /*-*/;
    --backdrop-blur: /*-*/ /*-*/;
    --backdrop-brightness: /*-*/ /*-*/;
    --backdrop-contrast: /*-*/ /*-*/;
    --backdrop-grayscale: /*-*/ /*-*/;
    --backdrop-hue-rotate: /*-*/ /*-*/;
    --backdrop-invert: /*-*/ /*-*/;
    --backdrop-opacity: /*-*/ /*-*/;
    --backdrop-saturate: /*-*/ /*-*/;
    --backdrop-sepia: /*-*/ /*-*/;
    --gradient-from-position: /*-*/ /*-*/;
    --gradient-to-position: /*-*/ /*-*/;
    --gradient-via-position: /*-*/ /*-*/;
    --scroll-snap-strictness: proximity;
    --border-spacing-x: 0;
    --border-spacing-y: 0;
    --translate-x: 0;
    --translate-y: 0;
    --rotate: 0;
    --rotate-x: 0;
    --rotate-y: 0;
    --skew-x: 0;
    --skew-y: 0;
    --scale-x: 1;
    --scale-y: 1;
}
}

@layer tokens{
  :where(:root, :host) {
    --aspect-ratios-square: 1 / 1;
    --aspect-ratios-landscape: 4 / 3;
    --aspect-ratios-portrait: 3 / 4;
    --aspect-ratios-wide: 16 / 9;
    --aspect-ratios-ultrawide: 18 / 5;
    --aspect-ratios-golden: 1.618 / 1;
    --borders-none: none;
    --easings-default: cubic-bezier(0.4, 0, 0.2, 1);
    --easings-linear: linear;
    --easings-in: cubic-bezier(0.4, 0, 1, 1);
    --easings-out: cubic-bezier(0, 0, 0.2, 1);
    --easings-in-out: cubic-bezier(0.4, 0, 0.2, 1);
    --durations-fastest: 50ms;
    --durations-faster: 100ms;
    --durations-fast: 150ms;
    --durations-normal: 200ms;
    --durations-slow: 300ms;
    --durations-slower: 400ms;
    --durations-slowest: 500ms;
    --radii-xs: 0.125rem;
    --radii-sm: 0.25rem;
    --radii-md: 0.375rem;
    --radii-lg: 0.5rem;
    --radii-xl: 0.75rem;
    --radii-2xl: 1rem;
    --radii-3xl: 1.5rem;
    --radii-4xl: 2rem;
    --radii-full: 9999px;
    --font-weights-thin: 100;
    --font-weights-extralight: 200;
    --font-weights-light: 300;
    --font-weights-normal: 400;
    --font-weights-medium: 500;
    --font-weights-semibold: 600;
    --font-weights-bold: 700;
    --font-weights-extrabold: 800;
    --font-weights-black: 900;
    --line-heights-none: 1;
    --line-heights-tight: 1.25;
    --line-heights-snug: 1.375;
    --line-heights-normal: 1.5;
    --line-heights-relaxed: 1.625;
    --line-heights-loose: 2;
    --letter-spacings-tighter: -0.05em;
    --letter-spacings-tight: -0.025em;
    --letter-spacings-normal: 0em;
    --letter-spacings-wide: 0.025em;
    --letter-spacings-wider: 0.05em;
    --letter-spacings-widest: 0.1em;
    --blurs-xs: 4px;
    --blurs-sm: 8px;
    --blurs-md: 12px;
    --blurs-lg: 16px;
    --blurs-xl: 24px;
    --blurs-2xl: 40px;
    --blurs-3xl: 64px;
    --spacing-0: 0rem;
    --spacing-1: 0.25rem;
    --spacing-2: 0.5rem;
    --spacing-3: 0.75rem;
    --spacing-4: 1rem;
    --spacing-5: 1.25rem;
    --spacing-6: 1.5rem;
    --spacing-7: 1.75rem;
    --spacing-8: 2rem;
    --spacing-9: 2.25rem;
    --spacing-10: 2.5rem;
    --spacing-11: 2.75rem;
    --spacing-12: 3rem;
    --spacing-14: 3.5rem;
    --spacing-16: 4rem;
    --spacing-20: 5rem;
    --spacing-24: 6rem;
    --spacing-28: 7rem;
    --spacing-32: 8rem;
    --spacing-36: 9rem;
    --spacing-40: 10rem;
    --spacing-44: 11rem;
    --spacing-48: 12rem;
    --spacing-52: 13rem;
    --spacing-56: 14rem;
    --spacing-60: 15rem;
    --spacing-64: 16rem;
    --spacing-72: 18rem;
    --spacing-80: 20rem;
    --spacing-96: 24rem;
    --spacing-0\.5: 0.125rem;
    --spacing-1\.5: 0.375rem;
    --spacing-2\.5: 0.625rem;
    --spacing-3\.5: 0.875rem;
    --spacing-4\.5: 1.125rem;
    --spacing-5\.5: 1.375rem;
    --colors-rose-50: #fff1f2;
    --colors-rose-100: #ffe4e6;
    --colors-rose-200: #fecdd3;
    --colors-rose-300: #fda4af;
    --colors-rose-400: #fb7185;
    --colors-rose-500: #f43f5e;
    --colors-rose-600: #e11d48;
    --colors-rose-700: #be123c;
    --colors-rose-800: #9f1239;
    --colors-rose-900: #881337;
    --colors-rose-950: #4c0519;
    --colors-fuchsia-50: #fdf4ff;
    --colors-fuchsia-100: #fae8ff;
    --colors-fuchsia-200: #f5d0fe;
    --colors-fuchsia-300: #f0abfc;
    --colors-fuchsia-400: #e879f9;
    --colors-fuchsia-500: #d946ef;
    --colors-fuchsia-600: #c026d3;
    --colors-fuchsia-700: #a21caf;
    --colors-fuchsia-800: #86198f;
    --colors-fuchsia-900: #701a75;
    --colors-fuchsia-950: #4a044e;
    --colors-violet-50: #f5f3ff;
    --colors-violet-100: #ede9fe;
    --colors-violet-200: #ddd6fe;
    --colors-violet-300: #c4b5fd;
    --colors-violet-400: #a78bfa;
    --colors-violet-500: #8b5cf6;
    --colors-violet-600: #7c3aed;
    --colors-violet-700: #6d28d9;
    --colors-violet-800: #5b21b6;
    --colors-violet-900: #4c1d95;
    --colors-violet-950: #2e1065;
    --colors-indigo-50: #eef2ff;
    --colors-indigo-100: #e0e7ff;
    --colors-indigo-200: #c7d2fe;
    --colors-indigo-300: #a5b4fc;
    --colors-indigo-400: #818cf8;
    --colors-indigo-500: #6366f1;
    --colors-indigo-600: #4f46e5;
    --colors-indigo-700: #4338ca;
    --colors-indigo-800: #3730a3;
    --colors-indigo-900: #312e81;
    --colors-indigo-950: #1e1b4b;
    --colors-sky-50: #f0f9ff;
    --colors-sky-100: #e0f2fe;
    --colors-sky-200: #bae6fd;
    --colors-sky-300: #7dd3fc;
    --colors-sky-400: #38bdf8;
    --colors-sky-500: #0ea5e9;
    --colors-sky-600: #0284c7;
    --colors-sky-700: #0369a1;
    --colors-sky-800: #075985;
    --colors-sky-900: #0c4a6e;
    --colors-sky-950: #082f49;
    --colors-emerald-50: #ecfdf5;
    --colors-emerald-100: #d1fae5;
    --colors-emerald-200: #a7f3d0;
    --colors-emerald-300: #6ee7b7;
    --colors-emerald-400: #34d399;
    --colors-emerald-500: #10b981;
    --colors-emerald-600: #059669;
    --colors-emerald-700: #047857;
    --colors-emerald-800: #065f46;
    --colors-emerald-900: #064e3b;
    --colors-emerald-950: #022c22;
    --colors-lime-50: #f7fee7;
    --colors-lime-100: #ecfccb;
    --colors-lime-200: #d9f99d;
    --colors-lime-300: #bef264;
    --colors-lime-400: #a3e635;
    --colors-lime-500: #84cc16;
    --colors-lime-600: #65a30d;
    --colors-lime-700: #4d7c0f;
    --colors-lime-800: #3f6212;
    --colors-lime-900: #365314;
    --colors-lime-950: #1a2e05;
    --colors-amber-50: #fffbeb;
    --colors-amber-100: #fef3c7;
    --colors-amber-200: #fde68a;
    --colors-amber-300: #fcd34d;
    --colors-amber-400: #fbbf24;
    --colors-amber-500: #f59e0b;
    --colors-amber-600: #d97706;
    --colors-amber-700: #b45309;
    --colors-amber-800: #92400e;
    --colors-amber-900: #78350f;
    --colors-amber-950: #451a03;
    --colors-neutral-50: #fafafa;
    --colors-neutral-100: #f5f5f5;
    --colors-neutral-200: #e5e5e5;
    --colors-neutral-300: #d4d4d4;
    --colors-neutral-400: #a3a3a3;
    --colors-neutral-500: #737373;
    --colors-neutral-600: #525252;
    --colors-neutral-700: #404040;
    --colors-neutral-800: #262626;
    --colors-neutral-900: #171717;
    --colors-neutral-950: #0a0a0a;
    --colors-stone-50: #fafaf9;
    --colors-stone-100: #f5f5f4;
    --colors-stone-200: #e7e5e4;
    --colors-stone-300: #d6d3d1;
    --colors-stone-400: #a8a29e;
    --colors-stone-500: #78716c;
    --colors-stone-600: #57534e;
    --colors-stone-700: #44403c;
    --colors-stone-800: #292524;
    --colors-stone-900: #1c1917;
    --colors-stone-950: #0c0a09;
    --colors-zinc-50: #fafafa;
    --colors-zinc-100: #f4f4f5;
    --colors-zinc-200: #e4e4e7;
    --colors-zinc-300: #d4d4d8;
    --colors-zinc-400: #a1a1aa;
    --colors-zinc-500: #71717a;
    --colors-zinc-600: #52525b;
    --colors-zinc-700: #3f3f46;
    --colors-zinc-800: #27272a;
    --colors-zinc-900: #18181b;
    --colors-zinc-950: #09090b;
    --colors-slate-50: #f8fafc;
    --colors-slate-100: #f1f5f9;
    --colors-slate-200: #e2e8f0;
    --colors-slate-300: #cbd5e1;
    --colors-slate-400: #94a3b8;
    --colors-slate-500: #64748b;
    --colors-slate-600: #475569;
    --colors-slate-700: #334155;
    --colors-slate-800: #1e293b;
    --colors-slate-900: #0f172a;
    --colors-slate-950: #020617;
    --colors-transparent: transparent;
    --colors-current: currentColor;
    --colors-black: #000000;
    --colors-white: #FFFFFF;
    --colors-white-alpha-50: rgba(255, 255, 255, 0.04);
    --colors-white-alpha-100: rgba(255, 255, 255, 0.06);
    --colors-white-alpha-200: rgba(255, 255, 255, 0.08);
    --colors-white-alpha-300: rgba(255, 255, 255, 0.16);
    --colors-white-alpha-400: rgba(255, 255, 255, 0.24);
    --colors-white-alpha-500: rgba(255, 255, 255, 0.36);
    --colors-white-alpha-600: rgba(255, 255, 255, 0.48);
    --colors-white-alpha-700: rgba(255, 255, 255, 0.64);
    --colors-white-alpha-800: rgba(255, 255, 255, 0.80);
    --colors-white-alpha-900: rgba(255, 255, 255, 0.92);
    --colors-black-alpha-50: rgba(0, 0, 0, 0.04);
    --colors-black-alpha-100: rgba(0, 0, 0, 0.06);
    --colors-black-alpha-200: rgba(0, 0, 0, 0.08);
    --colors-black-alpha-300: rgba(0, 0, 0, 0.16);
    --colors-black-alpha-400: rgba(0, 0, 0, 0.24);
    --colors-black-alpha-500: rgba(0, 0, 0, 0.36);
    --colors-black-alpha-600: rgba(0, 0, 0, 0.48);
    --colors-black-alpha-700: rgba(0, 0, 0, 0.64);
    --colors-black-alpha-800: rgba(0, 0, 0, 0.80);
    --colors-black-alpha-900: rgba(0, 0, 0, 0.92);
    --colors-gray-50: #F7FAFC;
    --colors-gray-100: #EDF2F7;
    --colors-gray-200: #E2E8F0;
    --colors-gray-300: #CBD5E0;
    --colors-gray-400: #A0AEC0;
    --colors-gray-500: #718096;
    --colors-gray-600: #4A5568;
    --colors-gray-700: #2D3748;
    --colors-gray-800: #1A202C;
    --colors-gray-900: #171923;
    --colors-gray-950: #030712;
    --colors-red-50: #FFF5F5;
    --colors-red-100: #FED7D7;
    --colors-red-200: #FEB2B2;
    --colors-red-300: #FC8181;
    --colors-red-400: #F56565;
    --colors-red-500: #E53E3E;
    --colors-red-600: #C53030;
    --colors-red-700: #9B2C2C;
    --colors-red-800: #822727;
    --colors-red-900: #63171B;
    --colors-red-950: #450a0a;
    --colors-orange-50: #FFFAF0;
    --colors-orange-100: #FEEBC8;
    --colors-orange-200: #FBD38D;
    --colors-orange-300: #F6AD55;
    --colors-orange-400: #ED8936;
    --colors-orange-500: #DD6B20;
    --colors-orange-600: #C05621;
    --colors-orange-700: #9C4221;
    --colors-orange-800: #7B341E;
    --colors-orange-900: #652B19;
    --colors-orange-950: #431407;
    --colors-yellow-50: #FFFFF0;
    --colors-yellow-100: #FEFCBF;
    --colors-yellow-200: #FAF089;
    --colors-yellow-300: #F6E05E;
    --colors-yellow-400: #ECC94B;
    --colors-yellow-500: #D69E2E;
    --colors-yellow-600: #B7791F;
    --colors-yellow-700: #975A16;
    --colors-yellow-800: #744210;
    --colors-yellow-900: #5F370E;
    --colors-yellow-950: #422006;
    --colors-green-50: #F0FFF4;
    --colors-green-100: #C6F6D5;
    --colors-green-200: #9AE6B4;
    --colors-green-300: #68D391;
    --colors-green-400: #48BB78;
    --colors-green-500: #38A169;
    --colors-green-600: #2F855A;
    --colors-green-700: #276749;
    --colors-green-800: #22543D;
    --colors-green-900: #1C4532;
    --colors-green-950: #052e16;
    --colors-teal-50: #E6FFFA;
    --colors-teal-100: #B2F5EA;
    --colors-teal-200: #81E6D9;
    --colors-teal-300: #4FD1C5;
    --colors-teal-400: #38B2AC;
    --colors-teal-500: #319795;
    --colors-teal-600: #2C7A7B;
    --colors-teal-700: #285E61;
    --colors-teal-800: #234E52;
    --colors-teal-900: #1D4044;
    --colors-teal-950: #042f2e;
    --colors-blue-50: #ebf8ff;
    --colors-blue-100: #bee3f8;
    --colors-blue-200: #90cdf4;
    --colors-blue-300: #63b3ed;
    --colors-blue-400: #4299e1;
    --colors-blue-500: #3182ce;
    --colors-blue-600: #2b6cb0;
    --colors-blue-700: #2c5282;
    --colors-blue-800: #2a4365;
    --colors-blue-900: #1A365D;
    --colors-blue-950: #172554;
    --colors-cyan-50: #EDFDFD;
    --colors-cyan-100: #C4F1F9;
    --colors-cyan-200: #9DECF9;
    --colors-cyan-300: #76E4F7;
    --colors-cyan-400: #0BC5EA;
    --colors-cyan-500: #00B5D8;
    --colors-cyan-600: #00A3C4;
    --colors-cyan-700: #0987A0;
    --colors-cyan-800: #086F83;
    --colors-cyan-900: #065666;
    --colors-cyan-950: #083344;
    --colors-purple-50: #FAF5FF;
    --colors-purple-100: #E9D8FD;
    --colors-purple-200: #D6BCFA;
    --colors-purple-300: #B794F4;
    --colors-purple-400: #9F7AEA;
    --colors-purple-500: #805AD5;
    --colors-purple-600: #6B46C1;
    --colors-purple-700: #553C9A;
    --colors-purple-800: #44337A;
    --colors-purple-900: #322659;
    --colors-purple-950: #3b0764;
    --colors-pink-50: #FFF5F7;
    --colors-pink-100: #FED7E2;
    --colors-pink-200: #FBB6CE;
    --colors-pink-300: #F687B3;
    --colors-pink-400: #ED64A6;
    --colors-pink-500: #D53F8C;
    --colors-pink-600: #B83280;
    --colors-pink-700: #97266D;
    --colors-pink-800: #702459;
    --colors-pink-900: #521B41;
    --colors-pink-950: #500724;
    --colors-brand-primary: #623875;
    --colors-brand-secondary: #b4b6c1;
    --colors-primary-100: #cec1d4;
    --colors-primary-300: #cec1d4;
    --colors-primary-500: #623875;
    --colors-primary-600: #462853;
    --colors-primary-main: #623875;
    --colors-primary-light: #cec1d4;
    --colors-primary-dark: #462853;
    --colors-greys-50: #f4f4f6;
    --colors-greys-100: #e7e8eb;
    --colors-greys-200: #b4b6c1;
    --colors-greys-300: #8f93a3;
    --colors-greys-400: #5c6278;
    --colors-greys-500: #3d435e;
    --colors-greys-600: #0c1436;
    --colors-greys-700: #0b1231;
    --colors-greys-white: #ffffff;
    --colors-secondary-50: #f4f4f6;
    --colors-secondary-100: #e7e8eb;
    --colors-secondary-200: #b4b6c1;
    --colors-secondary-300: #8f93a3;
    --colors-secondary-400: #5c6278;
    --colors-secondary-500: #3d435e;
    --colors-secondary-600: #0c1436;
    --colors-secondary-700: #0b1231;
    --colors-secondary-white: #ffffff;
    --colors-danger-400: #ffc1cc;
    --colors-danger-500: #ff375b;
    --colors-danger-600: #b52741;
    --colors-other-information-focus: #0671e0;
    --colors-other-divider-stroke: #b4b6c1;
    --colors-other-glass-stroke: #ffffff;
    --colors-product-accent-contact-centre-light: #dbacf5;
    --colors-product-accent-virtual-agents-main: #0f78c7;
    --colors-product-accent-virtual-agents-light: #60ade7;
    --colors-product-accent-conversation-intelligence-main: #ff9903;
    --colors-product-accent-conversation-intelligence-light: #fff3eb;
    --colors-product-accent-workforce-management-main: #00c06a;
    --colors-product-accent-workforce-management-light: #b0ebd1;
    --colors-product-accent-case-management-main: #c79ddf;
    --colors-product-accent-case-management-light: #f4e5fc;
    --colors-product-accent-agent-copilot-main: #33c4b3;
    --colors-product-accent-agent-copilot-light: #c0ede7;
    --colors-graph-primary-300: #2b8ec4;
    --colors-graph-primary-500: #216d97;
    --colors-graph-primary-600: #1a5779;
    --colors-graph-secondary-300: #ffc704;
    --colors-graph-secondary-500: #ff9903;
    --colors-graph-secondary-600: #cc7a02;
    --colors-graph-tertiary-300: #6dbeb1;
    --colors-graph-tertiary-500: #549288;
    --colors-graph-tertiary-600: #43756d;
    --colors-graph-quarternary-300: #ffe0ff;
    --colors-graph-quarternary-500: #dbacf5;
    --colors-graph-quarternary-600: #af8ac4;
    --colors-graph-quinary-300: #ff4876;
    --colors-graph-quinary-500: #ff375b;
    --colors-graph-quinary-600: #cc2c49;
    --colors-graph-senary-300: #ffb504;
    --colors-graph-senary-500: #e88b03;
    --colors-graph-senary-600: #ba6f02;
    --colors-graph-septenary-300: #d1000e;
    --colors-graph-septenary-500: #a1000b;
    --colors-graph-septenary-600: #810009;
    --colors-graph-octonary-300: #101a46;
    --colors-graph-octonary-500: #0c1436;
    --colors-graph-octonary-600: #0a102b;
    --colors-graph-nonary-300: #ebe1d9;
    --colors-graph-nonary-500: #b5ada7;
    --colors-graph-nonary-600: #918a86;
    --colors-graph-denary-300: #b66d03;
    --colors-graph-denary-500: #8c5402;
    --colors-graph-denary-600: #704302;
    --colors-signal-success: #33c4b3;
    --colors-signal-danger-error: #ff375b;
    --colors-signal-positive: #00884b;
    --colors-signal-negative: #B52741;
    --colors-signal-caution: #ff9903;
    --colors-signal-caution-light: #ffdfb1;
    --colors-signal-caution-dark: #b56d02;
    --colors-signal-danger-error-light: #ffc1cc;
    --colors-signal-danger-error-dark: #b52741;
    --colors-signal-success-light: #c0ede7;
    --colors-signal-success-dark: #248b7f;
    --colors-widget-background-blue: #e7f3fb;
    --colors-widget-background-sand: #e7e8eb;
    --colors-widget-background-green: #C0EDE7;
    --colors-widget-background-yellow: #FFDFB1;
    --colors-widget-background-red: #FFC1CC;
    --colors-productgradients-conversationalintelligence: linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, rgba(255, 153, 3, 0.40) 0%, rgba(255, 153, 3, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(255, 153, 3, 0.30) 0%, rgba(255, 153, 3, 0.00) 100%), #FFF;
    --colors-productgradients-virtual-agents: linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, #0F78C7 0%, rgba(15, 120, 199, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(96, 173, 231, 0.90) 0%, rgba(96, 173, 231, 0.00) 100%), linear-gradient(0deg, #B5D9F4 0%, #60ADE7 66%);
    --colors-gradients-ai-gradient-weak: linear-gradient(260deg, rgba(255, 153, 3, 0.00) 23.47%, rgba(255, 55, 91, 0.06) 47.05%, rgba(51, 196, 179, 0.00) 78.99%), linear-gradient(293deg, rgba(255, 55, 91, 0.00) 17.32%, rgba(255, 153, 3, 0.03) 48.33%, rgba(51, 196, 179, 0.06) 61.28%, rgba(51, 196, 179, 0.00) 85.76%), linear-gradient(270deg, #FFF 0%, #FAFAFA 100%);
    --colors-gradients-ai-gradient-strong: linear-gradient(0deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(180deg, rgba(255, 255, 255, 0.00) 43.85%, rgba(255, 255, 255, 0.75) 100%), radial-gradient(41.08% 58.46% at 50.27% 0%, rgba(255, 153, 3, 0.25) 0%, rgba(255, 153, 3, 0.00) 100%), radial-gradient(85.91% 84.32% at 0% 0%, rgba(51, 196, 179, 0.50) 0%, rgba(51, 196, 179, 0.00) 100%), radial-gradient(112.44% 70.52% at 100% 0%, rgba(219, 172, 245, 0.75) 0%, rgba(219, 172, 245, 0.00) 100%), #F4E5FC;
    --colors-gradients-ai-stroke-text: linear-gradient(0deg, #ff9cac 0%, #fecc82 49%, #9ce2d8 100%);
    --colors-gradients-dark: linear-gradient(0deg, rgba(98, 56, 117, 0.50) 0%, rgba(98, 56, 117, 0.50) 100%), radial-gradient(109.42% 85.6% at -2% -4.67%, rgba(51, 196, 179, 0.75) 0%, rgba(51, 196, 179, 0.00) 100%), radial-gradient(167.07% 153.23% at 130.67% 154.67%, rgba(255, 153, 3, 0.50) 0.19%, rgba(255, 153, 3, 0.25) 65%, rgba(255, 153, 3, 0.00) 100%), linear-gradient(91deg, #462853 0%, #623875 35%, #C79DDF 100%);
    --colors-gradients-light: linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, #623875 0%, rgba(98, 56, 117, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(51, 196, 179, 0.90) 0%, rgba(51, 196, 179, 0.00) 100%), linear-gradient(0deg, #EED9FA 0%, #DBACF5 66%);
    --colors-gradients-glass: linear-gradient(114deg, rgba(255, 255, 255, 0.60) 2.72%, rgba(255, 255, 255, 0.80) 100.25%);
    --colors-gradients-gold: linear-gradient(0deg, #ffd08b 0%, #e88b03 100%);
    --colors-graph-gradients-primary-fill: linear-gradient(0deg, #216d97 0%, #ffffff 100%);
    --colors-graph-gradients-primary-stroke: linear-gradient(0deg, #a9c6d7 0%, #216d97 100%);
    --colors-graph-gradients-secondary-fill: linear-gradient(0deg, #ff9903 0%, #ffffff 100%);
    --colors-graph-gradients-secondary-stroke: linear-gradient(0deg, #ffd08b 0%, #ff9903 100%);
    --colors-graph-gradients-tertiary-fill: linear-gradient(0deg, #549288 0%, #ffffff 100%);
    --colors-graph-gradients-tertiary-stroke: linear-gradient(0deg, #8ab4ad 0%, #549288 100%);
    --colors-graph-gradients-quaternary-fill: linear-gradient(0deg, #dbacf5 0%, #ffffff 100%);
    --colors-graph-gradients-quaternary-stroke: linear-gradient(0deg, #eed9fa 0%, #dbacf5 100%);
    --colors-graph-gradients-quinary-fill: linear-gradient(0deg, #ff375b 0%, #ffffff 100%);
    --colors-graph-gradients-quinary-stroke: linear-gradient(0deg, #ffa3b4 0%, #ff375b 100%);
    --colors-graph-gradients-senary-fill: linear-gradient(0deg, #e88b03 0%, #ffffff 100%);
    --colors-graph-gradients-senary-stroke: linear-gradient(0deg, #ffd08b 0%, #e88b03 100%);
    --colors-graph-gradients-septenary-fill: linear-gradient(0deg, #a1000b 0%, #ffffff 100%);
    --colors-graph-gradients-septenary-stroke: linear-gradient(0deg, #f28a91 0%, #a1000b 100%);
    --colors-graph-gradients-octonary-fill: linear-gradient(0deg, #0c1436 0%, #ffffff 100%);
    --colors-graph-gradients-octonary-stroke: linear-gradient(0deg, #8f93a3 0%, #0c1436 100%);
    --colors-graph-gradients-nonary-fill: linear-gradient(0deg, #b5ada7 0%, #ffffff 100%);
    --colors-graph-gradients-nonary-stroke: linear-gradient(0deg, #e8ddd6 0%, #b5ada7 100%);
    --colors-graph-gradients-denary-fill: linear-gradient(0deg, #8c5402 0%, #ffffff 100%);
    --colors-graph-gradients-denary-stroke: linear-gradient(0deg, #ffad35 0%, #8c5402 100%);
    --colors-segment-primary: #216d97;
    --colors-segment-secondary: #ff9903;
    --colors-segment-tertiary: #549288;
    --colors-segment-quaternary: #dbacf5;
    --colors-segment-quinary: #ff375b;
    --colors-segment-senary: #e88b03;
    --colors-segments-primary-300: #2b8ec4;
    --colors-segments-primary-500: #216d97;
    --colors-segments-primary-600: #1a5779;
    --colors-segments-secondary-300: #ffc704;
    --colors-segments-secondary-500: #ff9903;
    --colors-segments-secondary-600: #cc7a02;
    --colors-segments-tertiary-300: #6dbeb1;
    --colors-segments-tertiary-500: #549288;
    --colors-segments-tertiary-600: #43756d;
    --colors-segments-quaternary-300: #ffe0ff;
    --colors-segments-quaternary-500: #dbacf5;
    --colors-segments-quaternary-600: #af8ac4;
    --colors-segments-quinary-300: #ff4876;
    --colors-segments-quinary-500: #ff375b;
    --colors-segments-quinary-600: #cc2c49;
    --colors-segments-senary-300: #ffb504;
    --colors-segments-senary-500: #e88b03;
    --colors-segments-senary-600: #ba6f02;
    --colors-subtitle: #8f93a3;
    --colors-border-light: #e7e8eb;
    --colors-outline-color: #62387533;
    --colors-cases-inbound: linear-gradient(131deg, #FBF4E5 7.57%, #FEEBC8 86.83%);
    --colors-cases-outbound: linear-gradient(107deg, #EEF7FB 10.21%, #E2F0F9 66.35%);
    --colors-cases-auto-reply: linear-gradient(243deg, #FAFAFA 11.91%, #F5F5F5 81.22%);
    --colors-workspace-body-background: #ffffff;
    --colors-workspace-content-background: linear-gradient(114deg, rgba(255, 255, 255, 0.60) 2.72%, rgba(255, 255, 255, 0.80) 100.25%);
    --colors-workspace-content-border: #ffffff;
    --colors-workspace-sidebar-darkbg: linear-gradient(0deg, rgba(98, 56, 117, 0.50) 0%, rgba(98, 56, 117, 0.50) 100%), radial-gradient(109.42% 85.6% at -2% -4.67%, rgba(51, 196, 179, 0.75) 0%, rgba(51, 196, 179, 0.00) 100%), radial-gradient(167.07% 153.23% at 130.67% 154.67%, rgba(255, 153, 3, 0.50) 0.19%, rgba(255, 153, 3, 0.25) 65%, rgba(255, 153, 3, 0.00) 100%), linear-gradient(91deg, #462853 0%, #623875 35%, #C79DDF 100%);
    --colors-workspace-sidebar-lightbg: linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, rgba(255, 153, 3, 0.40) 0%, rgba(255, 153, 3, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(255, 153, 3, 0.30) 0%, rgba(255, 153, 3, 0.00) 100%), #FFF;
    --colors-workspace-sidebar-text: #0c1436;
    --colors-workspace-sidebar-header-text: #5c6278;
    --colors-card-bg: #ffffff;
    --colors-card-header-title: #0c1436;
    --colors-card-header-subtitle: #5c6278;
    --colors-card-border-color: #e7e8eb;
    --colors-success: #33c4b3;
    --colors-warning: #ff9903;
    --colors-warning-dark: #b56d02;
    --colors-warning-light: #ffdfb1;
    --colors-info: #8f93a3;
    --colors-positive: #00884b;
    --colors-negative: #B52741;
    --colors-text-muted: #5c6278;
    --colors-text: #0c1436;
    --colors-google: #4285F4;
    --colors-microsoft: #DC3E15;
    --shadows-2xs: 0 1px rgb(0 0 0 / 0.05);
    --shadows-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadows-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    --shadows-inset-2xs: inset 0 1px rgb(0 0 0 / 0.05);
    --shadows-inset-xs: inset 0 1px 1px rgb(0 0 0 / 0.05);
    --shadows-inset-sm: inset 0 2px 4px rgb(0 0 0 / 0.05);
    --shadows-base: 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04);
    --shadows-md: 0px 10px 20px rgba(0, 0, 0, 0.04), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
    --shadows-lg: 0px 16px 24px rgba(0, 0, 0, 0.06), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
    --shadows-extended: 0px 1px 1px 0px rgba(0, 0, 0, 0.10), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 5px 3px 0px rgba(0, 0, 0, 0.05), 0px 10px 4px 0px rgba(0, 0, 0, 0.01), 0px 15px 4px 0px rgba(0, 0, 0, 0.00);
    --shadows-text-extended: 0px 15px 4px rgba(0, 0, 0, 0.00), 0px 10px 4px rgba(0, 0, 0, 0.01), 0px 5px 3px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.09), 0px 1px 1px rgba(0, 0, 0, 0.10);
    --shadows-xl: 0px 24px 32px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
    --shadows-2xl: 0px 36px 80px rgba(0, 0, 0, 0.07), 0px 23.3333px 46.8519px rgba(0, 0, 0, 0.0531481), 0px 13.8667px 25.4815px rgba(0, 0, 0, 0.0425185), 0px 7.2px 13px rgba(0, 0, 0, 0.035), 0px 2.93333px 6.51852px rgba(0, 0, 0, 0.0274815), 0px 0.666667px 3.14815px rgba(0, 0, 0, 0.0168519);
    --shadows-outline: 0 0 0 3px #62387533;
    --shadows-input-outline: 0px 2px 0px 0px #e7e8eb;
    --shadows-kbd-inner: 0px -2px 0px 0px #E2E8F0 inset;
    --shadows-dark-lg: rgba(0, 0, 0, 0.1) 0px 0px 0px 1px,rgba(0, 0, 0, 0.2) 0px 5px 10px,rgba(0, 0, 0, 0.4) 0px 15px 40px;
    --shadows-glass-shadow: 89px 44px 28px 0px rgba(70, 40, 83, 0.00), 57px 28px 25px 0px rgba(70, 40, 83, 0.01), 32px 16px 21px 0px rgba(70, 40, 83, 0.03), 14px 7px 16px 0px rgba(70, 40, 83, 0.04), 4px 2px 9px 0px rgba(70, 40, 83, 0.05);
    --fonts-sans: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
    --fonts-serif: ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
    --fonts-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    --fonts-body: "Poppins", sans-serif;
    --fonts-heading: "Noto Serif Display", sans-serif;
    --font-sizes-2xs: 0.5rem;
    --font-sizes-7xl: 4.5rem;
    --font-sizes-8xl: 6rem;
    --font-sizes-9xl: 8rem;
    --font-sizes-xs: 0.6875rem;
    --font-sizes-sm: 0.75rem;
    --font-sizes-md: 0.875rem;
    --font-sizes-lg: 1rem;
    --font-sizes-xl: 1.125rem;
    --font-sizes-2xl: 1.25rem;
    --font-sizes-3xl: 1.5rem;
    --font-sizes-4xl: 1.875rem;
    --font-sizes-5xl: 2.25rem;
    --font-sizes-6xl: 3rem;
    --sizes-0: 0rem;
    --sizes-1: 0.25rem;
    --sizes-2: 0.5rem;
    --sizes-3: 0.75rem;
    --sizes-4: 1rem;
    --sizes-5: 1.25rem;
    --sizes-6: 1.5rem;
    --sizes-7: 1.75rem;
    --sizes-8: 2rem;
    --sizes-9: 2.25rem;
    --sizes-10: 2.5rem;
    --sizes-11: 2.75rem;
    --sizes-12: 3rem;
    --sizes-14: 3.5rem;
    --sizes-16: 4rem;
    --sizes-20: 5rem;
    --sizes-24: 6rem;
    --sizes-28: 7rem;
    --sizes-32: 8rem;
    --sizes-36: 9rem;
    --sizes-40: 10rem;
    --sizes-44: 11rem;
    --sizes-48: 12rem;
    --sizes-52: 13rem;
    --sizes-56: 14rem;
    --sizes-60: 15rem;
    --sizes-64: 16rem;
    --sizes-72: 18rem;
    --sizes-80: 20rem;
    --sizes-96: 24rem;
    --sizes-4\.5: 1.125rem;
    --sizes-5\.5: 1.375rem;
    --sizes-fit: fit-content;
    --sizes-px: 1px;
    --sizes-0\.5: 0.125rem;
    --sizes-1\.5: 0.375rem;
    --sizes-2\.5: 0.625rem;
    --sizes-3\.5: 0.875rem;
    --sizes-max: max-content;
    --sizes-min: min-content;
    --sizes-full: 100%;
    --sizes-3xs: 14rem;
    --sizes-2xs: 16rem;
    --sizes-xs: 20rem;
    --sizes-sm: 24rem;
    --sizes-md: 28rem;
    --sizes-lg: 32rem;
    --sizes-xl: 36rem;
    --sizes-2xl: 42rem;
    --sizes-3xl: 48rem;
    --sizes-4xl: 56rem;
    --sizes-5xl: 64rem;
    --sizes-6xl: 72rem;
    --sizes-7xl: 80rem;
    --sizes-8xl: 90rem;
    --sizes-prose: 60ch;
    --sizes-container-sm: 640px;
    --sizes-container-md: 768px;
    --sizes-container-lg: 1024px;
    --sizes-container-xl: 1280px;
    --sizes-container-2xl: 1536px;
    --sizes-breakpoint-sm: 30em;
    --sizes-breakpoint-md: 48em;
    --sizes-breakpoint-lg: 62em;
    --sizes-breakpoint-xl: 80em;
    --sizes-breakpoint-1\.5xl: 85em;
    --sizes-breakpoint-2xl: 96em;
    --sizes-breakpoint-3xl: 118.75em;
    --animations-spin: spin 1s linear infinite;
    --animations-ping: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    --animations-pulse: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    --animations-bounce: bounce 1s infinite;
    --animations-fade-in-sm: fadeIn 140ms ease-in both;
    --animations-fade-color: fadeColor 2s ease 250ms;
    --breakpoints-sm: 30em;
    --breakpoints-md: 48em;
    --breakpoints-lg: 62em;
    --breakpoints-xl: 80em;
    --breakpoints-1\.5xl: 85em;
    --breakpoints-2xl: 96em;
    --breakpoints-3xl: 118.75em;
}

  @keyframes spin {
    to {
      transform: rotate(360deg);
}
}

  @keyframes ping {
    75%,100% {
      transform: scale(2);
      opacity: 0;
}
}

  @keyframes pulse {
    50% {
      opacity: 0.5;
}
}

  @keyframes bounce {
    0%,100% {
      transform: translateY(-25%);
      animation-timing-function: cubic-bezier(0.8,0,1,1);
}

    50% {
      transform: none;
      animation-timing-function: cubic-bezier(0,0,0.2,1);
}
}

  @keyframes fadeIn {
    from {
      opacity: 0;
}

    to {
      opacity: 1;
}
}

  @keyframes fadeColor {
    from {
      background-color: var(--fade-color-start);
}

    to {
      background-color: var(--fade-color-end);
}
}
}

@layer recipes{
  @layer _base{

    .collapsible-sidebar {
      background: var(--colors-workspace-sidebar-lightbg);
      transition: box-shadow 200ms ease-out, transform 200ms ease-out;
      border-right: 2px solid;
      z-index: 1;
      color: var(--colors-workspace-sidebar-text);
      border-image-source: linear-gradient(180deg, rgba(239,58,66,0.5) 0%, rgba(255,178,32,0.5) 49%, rgba(255,145,179,0.5) 100%);
      border-image-slice: 1;
      transform: translateX(var(--sidebar-x, 0%));
      height: 100vh;
      left: var(--spacing-0);
      top: var(--spacing-0);
      bottom: var(--spacing-0);
}

    .magic-box {
      background: var(--colors-white);
      border-radius: var(--magic-box-radius, var(--radii-md));
      position: relative;
      color: var(--colors-text);
      box-shadow: var(--shadows-glass-shadow);
}

    .magic-box::before {
      inset: var(--spacing-0);
      background: linear-gradient(180deg, rgba(255,55,91,0.5) 0%, rgba(255,153,3,0.5) 49.48%, rgba(51,196,179,0.5) 100%);
      padding: 2px;
      border-radius: inherit;
      mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      content: "";
      position: absolute;
      pointer-events: none;
      mask-composite: exclude;
      -webkit-mask-composite: xor;
}
}

  .collapsible-sidebar--open_true {
    --sidebar-x: 0%;
    box-shadow: var(--shadows-dark-lg);
}

  .collapsible-sidebar--showAsOverlay_true {
    position: fixed;
}

  .collapsible-sidebar--open_false {
    --sidebar-x: -95%;
}

  .magic-box--rounded_md {
    --magic-box-radius: var(--radii-md);
}

  .magic-box--elevation_glass {
    box-shadow: var(--shadows-glass-shadow);
}

  .magic-box--padded_true {
    padding: var(--spacing-4);
}

  .magic-box--rounded_sm {
    --magic-box-radius: var(--radii-sm);
}

  .magic-box--rounded_lg {
    --magic-box-radius: var(--radii-lg);
}

  .magic-box--rounded_xl {
    --magic-box-radius: var(--radii-xl);
}

  .magic-box--elevation_none {
    box-shadow: none;
}

  .magic-box--elevation_sm {
    box-shadow: var(--shadows-sm);
}

  .magic-box--elevation_md {
    box-shadow: var(--shadows-md);
}

  .magic-box--elevation_lg {
    box-shadow: var(--shadows-lg);
}

  .magic-box--padded_false {
    padding: var(--spacing-0);
}

  @media screen and (min-width: 85rem) {
    .collapsible-sidebar--open_true {
      box-shadow: none;
}
}
}

@layer utilities{

  .\--thickness_1px {
    --thickness: 1px;
}

  .bg_white {
    background: var(--colors-white);
}

  .bg_repeating-radial-gradient\(_circle_at_0_0\,_transparent_0\,_\#ffbede_9px_\)\,_repeating-linear-gradient\(_\#f7a8dd55\,_\#f7a8dd_\)\; {
    background: repeating-radial-gradient( circle at 0 0, transparent 0, #ffbede 9px ), repeating-linear-gradient( #f7a8dd55, #f7a8dd );;
}

  .bg_workspace\.contentBackground {
    background: var(--colors-workspace-content-background);
}

  .border_1px_solid {
    border: 1px solid;
}

  .m_-10px {
    margin: -10px;
}

  .p_8 {
    padding: var(--spacing-8);
}

  .bg_workspace\.bodyBackground {
    background: var(--colors-workspace-body-background);
}

  .p_4 {
    padding: var(--spacing-4);
}

  .p_2 {
    padding: var(--spacing-2);
}

  .p_0 {
    padding: var(--spacing-0);
}

  .border_0 {
    border-width: 0;
    border-style: solid;
}

  .bg_greys\.100 {
    background: var(--colors-greys-100);
}

  .border_none {
    border: var(--borders-none);
}

  .bg_greys\.50 {
    background: var(--colors-greys-50);
}

  .bg_signal\.cautionLight {
    background: var(--colors-signal-caution-light);
}

  .bg_warning {
    background: var(--colors-warning);
}

  .p_1 {
    padding: var(--spacing-1);
}

  .m_0 {
    margin: var(--spacing-0);
}

  .bg_primary\.500 {
    background: var(--colors-primary-500);
}

  .border_2px_solid {
    border: 2px solid;
}

  .bg_workspace\.sidebar\.lightbg {
    background: var(--colors-workspace-sidebar-lightbg);
}

  .border_0\.2em_solid {
    border: 0.2em solid;
}

  .m_5rem_auto {
    margin: 5rem auto;
}

  .m_1rem_auto {
    margin: 1rem auto;
}

  .border_1px_solid_white {
    border: 1px solid white;
}

  .bg_success {
    background: var(--colors-success);
}

  .bg_greys\.white {
    background: var(--colors-greys-white);
}

  .border_3px_dashed {
    border: 3px dashed;
}

  .bg_gradients\.aiGradientWeak {
    background: var(--colors-gradients-ai-gradient-weak);
}

  .bg_none {
    background: none;
}

  .border_1px_solid_\#2F96D0 {
    border: 1px solid #2F96D0;
}

  .m_4 {
    margin: var(--spacing-4);
}

  .grid-area_audio-player__Users {
    grid-area: audio-player__Users;
}

  .grid-area_audio-player__user-percentage {
    grid-area: audio-player__user-percentage;
}

  .grid-area_audio-player__user-seeker {
    grid-area: audio-player__user-seeker;
}

  .border_1px {
    border: solid 1px;
}

  .bg_greys\.200 {
    background: var(--colors-greys-200);
}

  .bg_signal\.positive {
    background: var(--colors-signal-positive);
}

  .bg_signal\.negative {
    background: var(--colors-signal-negative);
}

  .bg_transparent {
    background: var(--colors-transparent);
}

  .bg_greys\.500 {
    background: var(--colors-greys-500);
}

  .bg_green\.400 {
    background: var(--colors-green-400);
}

  .bg_signal\.caution {
    background: var(--colors-signal-caution);
}

  .m_1px {
    margin: 1px;
}

  .border_1px_solid_transparent {
    border: 1px solid transparent;
}

  .m_1 {
    margin: var(--spacing-1);
}

  .border_1px_solid_var\(--chakra-colors-primary-500\) {
    border: 1px solid var(--chakra-colors-primary-500);
}

  .border_solid_1px {
    border: solid 1px;
}

  .bg_segments\.primary\.500 {
    background: var(--colors-segments-primary-500);
}

  .bg_graph\.primary\.300 {
    background: var(--colors-graph-primary-300);
}

  .m_0_auto {
    margin: 0 auto;
}

  .bg_primary\.light {
    background: var(--colors-primary-light);
}

  .bd-x_1px_solid {
    border-inline: 1px solid;
}

  .bg_greys\.600 {
    background: var(--colors-greys-600);
}

  .m_10vh_auto {
    margin: 10vh auto;
}

  .m_2 {
    margin: var(--spacing-2);
}

  .p_6 {
    padding: var(--spacing-6);
}

  .bg_linear-gradient\(270deg\,_\#F5F5F5_86\.02\%\,_rgba\(245\,_245\,_245\,_0\.00\)_100\%\) {
    background: linear-gradient(270deg, #F5F5F5 86.02%, rgba(245, 245, 245, 0.00) 100%);
}

  .bg_greys\.400 {
    background: var(--colors-greys-400);
}

  .bg_linear-gradient\(180deg\,_rgba\(255\,_255\,_255\,_0\)_0\%\,_white_100\%\) {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, white 100%);
}

  .grid-area_divider {
    grid-area: divider;
}

  .bg_gradients\.dark {
    background: var(--colors-gradients-dark);
}

  .bg_gradients\.aiGradientStrong {
    background: var(--colors-gradients-ai-gradient-strong);
}

  .bg_gray\.300 {
    background: var(--colors-gray-300);
}

  .bg_inherit {
    background: inherit;
}

  .p_3 {
    padding: var(--spacing-3);
}

  .bg_primary\.50 {
    background: primary.50;
}

  .p_8px {
    padding: 8px;
}

  .bg_linear-gradient\(135deg\,_\#FFF_0\%\,_\#EAEAEA_100\%\) {
    background: linear-gradient(135deg, #FFF 0%, #EAEAEA 100%);
}

  .p_0\.5 {
    padding: var(--spacing-0\.5);
}

  .bg_gradients\.gold {
    background: var(--colors-gradients-gold);
}

  .m_3 {
    margin: var(--spacing-3);
}

  .border_2px_dashed {
    border: 2px dashed;
}

  .bg_warningLight {
    background: var(--colors-warning-light);
}

  .border_3px_solid {
    border: 3px solid;
}

  .bd-y_1px_solid {
    border-block: 1px solid;
}

  .bg_orange\.50 {
    background: var(--colors-orange-50);
}

  .bg_cases\.inbound {
    background: var(--colors-cases-inbound);
}

  .border_initial {
    border: solid initial;
}

  .p_16px_16px {
    padding: 16px 16px;
}

  .p_16px_0 {
    padding: 16px 0;
}

  .p_initial {
    padding: initial;
}

  .border_greys\.50_1px_solid {
    border: greys.50 1px solid;
}

  .bg_green\.100 {
    background: var(--colors-green-100);
}

  .m_4rem_auto {
    margin: 4rem auto;
}

  .bg_\#C1D2DC {
    background: #C1D2DC;
}

  .bg_danger {
    background: danger;
}

  .bg_gray\.100 {
    background: var(--colors-gray-100);
}

  .border_1px_solid_red {
    border: 1px solid red;
}

  .bg_blue\.400 {
    background: var(--colors-blue-400);
}

  .bg_greys\.300 {
    background: var(--colors-greys-300);
}

  .bg_primary\.100 {
    background: var(--colors-primary-100);
}

  .bg_primary\.200 {
    background: primary.200;
}

  .grid-area_guide {
    grid-area: guide;
}

  .grid-area_config {
    grid-area: config;
}

  .bg_card\.bg {
    background: var(--colors-card-bg);
}

  .bg_gray\.400 {
    background: var(--colors-gray-400);
}

  .bg_gray\.200 {
    background: var(--colors-gray-200);
}

  .border_solid_2px_\#FF5C5C {
    border: solid 2px #FF5C5C;
}

  .border_solid_2px_\#df3f3f {
    border: solid 2px #df3f3f;
}

  .m_8 {
    margin: var(--spacing-8);
}

  .bg_linear-gradient\(180deg\,rgba\(255\,_255\,_255\,_0\.33\)_0\%\,_rgba\(255\,_255\,_255\,_1\)_60\%\) {
    background: linear-gradient(180deg,rgba(255, 255, 255, 0.33) 0%, rgba(255, 255, 255, 1) 60%);
}

  .gap_10 {
    gap: var(--spacing-10);
}

  .flex_0_0_40px {
    flex: 0 0 40px;
}

  .flex_1 {
    flex: 1 1 0%;
}

  .px_4 {
    padding-inline: var(--spacing-4);
}

  .gap_8px {
    gap: 8px;
}

  .ov_auto {
    overflow: auto;
}

  .gap_4 {
    gap: var(--spacing-4);
}

  .border-r_none {
    border-right: var(--borders-none);
}

  .bd-c_workspace\.contentBorder {
    border-color: var(--colors-workspace-content-border);
}

  .bdr_8 {
    border-radius: 8px;
}

  .px_6 {
    padding-inline: var(--spacing-6);
}

  .py_8 {
    padding-block: var(--spacing-8);
}

  .flex_0_0_auto {
    flex: 0 0 auto;
}

  .mx_auto {
    margin-inline: auto;
}

  .px_2 {
    padding-inline: var(--spacing-2);
}

  .ov_hidden {
    overflow: hidden;
}

  .my_0 {
    margin-block: var(--spacing-0);
}

  .ring_1px_solid {
    outline: 1px solid;
}

  .bdr_4px {
    border-radius: 4px;
}

  .border-b_1px_solid {
    border-bottom: 1px solid;
}

  .flex_0_1_auto {
    flex: 0 1 auto;
}

  .border-b_1px {
    border-bottom: solid 1px;
}

  .gap_0 {
    gap: var(--spacing-0);
}

  .mx_2 {
    margin-inline: var(--spacing-2);
}

  .bdr_base {
    border-radius: base;
}

  .my_4 {
    margin-block: var(--spacing-4);
}

  .bdr_md {
    border-radius: var(--radii-md);
}

  .py_2 {
    padding-block: var(--spacing-2);
}

  .ov_hidden_auto {
    overflow: hidden auto;
}

  .bdr_0px {
    border-radius: 0px;
}

  .flex_1_0_auto {
    flex: 1 0 auto;
}

  .gap_1 {
    gap: var(--spacing-1);
}

  .td_none {
    text-decoration: none;
}

  .px_8 {
    padding-inline: var(--spacing-8);
}

  .py_1 {
    padding-block: var(--spacing-1);
}

  .mx_1 {
    margin-inline: var(--spacing-1);
}

  .mx_4 {
    margin-inline: var(--spacing-4);
}

  .bdr_full {
    border-radius: var(--radii-full);
}

  .bd-c_primary\.500 {
    border-color: var(--colors-primary-500);
}

  .py_5px {
    padding-block: 5px;
}

  .gap_1\.5 {
    gap: var(--spacing-1\.5);
}

  .bd-w_1 {
    border-width: 1px;
}

  .bdr_2xl {
    border-radius: var(--radii-2xl);
}

  .trs_all_0\.2s_ease-in-out {
    transition: all 0.2s ease-in-out;
}

  .px_0 {
    padding-inline: var(--spacing-0);
}

  .px_8px {
    padding-inline: 8px;
}

  .ov_scroll {
    overflow: scroll;
}

  .bd-c_white {
    border-color: var(--colors-white);
}

  .trs_opacity_200ms_ease {
    transition: opacity 200ms ease;
}

  .py_6 {
    padding-block: var(--spacing-6);
}

  .bdr_0 {
    border-radius: 0;
}

  .gap_2 {
    gap: var(--spacing-2);
}

  .my_16 {
    margin-block: var(--spacing-16);
}

  .gap_3 {
    gap: var(--spacing-3);
}

  .flex_1_1 {
    flex: 1 1;
}

  .ov_visible {
    overflow: visible;
}

  .bdr_999 {
    border-radius: 999px;
}

  .trs_transform_0\.3s_ease {
    transition: transform 0.3s ease;
}

  .gap_16 {
    gap: var(--spacing-16);
}

  .flex_1_1_auto {
    flex: 1 1 auto;
}

  .bdr_2 {
    border-radius: 2px;
}

  .bdr_0\.25rem {
    border-radius: 0.25rem;
}

  .bdr_9999px {
    border-radius: 9999px;
}

  .py_4 {
    padding-block: var(--spacing-4);
}

  .gap_8 {
    gap: var(--spacing-8);
}

  .bdr_lg {
    border-radius: var(--radii-lg);
}

  .my_8 {
    margin-block: var(--spacing-8);
}

  .gap_6 {
    gap: var(--spacing-6);
}

  .flex_35 {
    flex: 35;
}

  .flex_65 {
    flex: 65;
}

  .gap_0\.5 {
    gap: var(--spacing-0\.5);
}

  .border-t_none {
    border-top: var(--borders-none);
}

  .border-b_2px_solid {
    border-bottom: 2px solid;
}

  .border-t_1px {
    border-top: solid 1px;
}

  .trs_200ms_ease-in-out {
    transition: 200ms ease-in-out;
}

  .trs_all_700ms {
    transition: all 700ms;
}

  .gap_2\.5 {
    gap: var(--spacing-2\.5);
}

  .bd-c_greys\.600 {
    border-color: var(--colors-greys-600);
}

  .bdr_16px {
    border-radius: 16px;
}

  .mx_2px {
    margin-inline: 2px;
}

  .mx_-2px {
    margin-inline: -2px;
}

  .trs_all_\.2s_ease-in-out {
    transition: all .2s ease-in-out;
}

  .bdr_4 {
    border-radius: 4px;
}

  .bd-c_greys\.200 {
    border-color: var(--colors-greys-200);
}

  .flex_1_0_40\% {
    flex: 1 0 40%;
}

  .px_3 {
    padding-inline: var(--spacing-3);
}

  .gap_60px {
    gap: 60px;
}

  .py_3 {
    padding-block: var(--spacing-3);
}

  .px_2px {
    padding-inline: 2px;
}

  .px_5 {
    padding-inline: var(--spacing-5);
}

  .py_0\.875rem {
    padding-block: 0.875rem;
}

  .bdr_0px_0px_10px_10px {
    border-radius: 0px 0px 10px 10px;
}

  .bd-c_border\.light {
    border-color: var(--colors-border-light);
}

  .bdr_10px_10px_0px_0px {
    border-radius: 10px 10px 0px 0px;
}

  .px_1 {
    padding-inline: var(--spacing-1);
}

  .border-style_dashed {
    border-style: dashed;
}

  .bd-c_greys\.300 {
    border-color: var(--colors-greys-300);
}

  .bdr_2px {
    border-radius: 2px;
}

  .bdr_xl {
    border-radius: var(--radii-xl);
}

  .bdr_sm {
    border-radius: var(--radii-sm);
}

  .flex_0_0_50\% {
    flex: 0 0 50%;
}

  .flex_initial {
    flex: 0 1 auto;
}

  .py_0 {
    padding-block: var(--spacing-0);
}

  .gap_1px {
    gap: 1px;
}

  .bd-c_transparent {
    border-color: var(--colors-transparent);
}

  .flex_0 {
    flex: 0;
}

  .border-l_1px_solid_var\(--chakra-colors-greys-100\) {
    border-left: 1px solid var(--chakra-colors-greys-100);
}

  .border-l_inherit {
    border-left: solid inherit;
}

  .border-r_1px_solid_var\(--chakra-colors-greys-100\) {
    border-right: 1px solid var(--chakra-colors-greys-100);
}

  .border-r_inherit {
    border-right: solid inherit;
}

  .bd-c_none {
    border-color: none;
}

  .ring_0 {
    outline: 0;
}

  .bdr_50px {
    border-radius: 50px;
}

  .bg-p_bottom\! {
    background-position: bottom !important;
}

  .td_underline {
    text-decoration: underline;
}

  .flex_3 {
    flex: 3;
}

  .flex_2 {
    flex: 2;
}

  .flex_80\% {
    flex: 80%;
}

  .flex_20\% {
    flex: 20%;
}

  .border-t_1px_solid {
    border-top: 1px solid;
}

  .px_20 {
    padding-inline: var(--spacing-20);
}

  .trs_all_100ms_linear {
    transition: all 100ms linear;
}

  .grid-c_span_1 {
    grid-column: span 1;
}

  .grid-r_span_2 {
    grid-row: span 2;
}

  .grid-r_span_1 {
    grid-row: span 1;
}

  .my_6 {
    margin-block: var(--spacing-6);
}

  .my_2 {
    margin-block: var(--spacing-2);
}

  .trs_all_200ms_ease-out {
    transition: all 200ms ease-out;
}

  .trs_visibility_200ms_linear\,opacity_200ms_linear {
    transition: visibility 200ms linear,opacity 200ms linear;
}

  .border-b_none {
    border-bottom: var(--borders-none);
}

  .columns_3 {
    columns: 3;
}

  .mx_0 {
    margin-inline: var(--spacing-0);
}

  .grid-c_span_2 {
    grid-column: span 2;
}

  .bd-w_1px {
    border-width: 1px;
}

  .bdr_50\% {
    border-radius: 50%;
}

  .trs_opacity_0\.2s {
    transition: opacity 0.2s;
}

  .my_1 {
    margin-block: var(--spacing-1);
}

  .mx_50\% {
    margin-inline: 50%;
}

  .my_20 {
    margin-block: var(--spacing-20);
}

  .my_36 {
    margin-block: var(--spacing-36);
}

  .my_6px {
    margin-block: 6px;
}

  .bd-c_danger {
    border-color: danger;
}

  .border-r_1px {
    border-right: solid 1px;
}

  .px_0\.5 {
    padding-inline: var(--spacing-0\.5);
}

  .py_0\.5 {
    padding-block: var(--spacing-0\.5);
}

  .trs_left_160ms_ease-out\,_width_160ms_ease-out {
    transition: left 160ms ease-out, width 160ms ease-out;
}

  .flex_0_0_10rem {
    flex: 0 0 10rem;
}

  .flex_15\% {
    flex: 15%;
}

  .flex_10\% {
    flex: 10%;
}

  .flex_30\% {
    flex: 30%;
}

  .bdr_3px {
    border-radius: 3px;
}

  .trs_height_200ms {
    transition: height 200ms;
}

  .trs_height_300ms {
    transition: height 300ms;
}

  .bdr_100\% {
    border-radius: 100%;
}

  .my_3 {
    margin-block: var(--spacing-3);
}

  .border-l_4px_gold_solid {
    border-left: 4px gold solid;
}

  .border-b_2px {
    border-bottom: solid 2px;
}

  .bd-c_greys\.400 {
    border-color: var(--colors-greys-400);
}

  .gap_4rem {
    gap: 4rem;
}

  .gap_1rem {
    gap: 1rem;
}

  .gap_clamp\(32px\,_3vw\,_48px\) {
    gap: clamp(32px, 3vw, 48px);
}

  .trs_all_0\.3s_ease-out {
    transition: all 0.3s ease-out;
}

  .ring_none\! {
    outline: var(--borders-none) !important;
}

  .trs_height_0\.2s_ease-in-out {
    transition: height 0.2s ease-in-out;
}

  .py_1\.5 {
    padding-block: var(--spacing-1\.5);
}

  .trs_background_200ms {
    transition: background 200ms;
}

  .bdr_999px {
    border-radius: 999px;
}

  .my_auto {
    margin-block: auto;
}

  .trs_opacity_0\.5s_ease-out\; {
    transition: opacity 0.5s ease-out;;
}

  .flex_1_0_50\% {
    flex: 1 0 50%;
}

  .trs_transform_0\.2s_ease-in-out {
    transition: transform 0.2s ease-in-out;
}

  .flex_1_0_30\% {
    flex: 1 0 30%;
}

  .border-l_2px {
    border-left: solid 2px;
}

  .border-r_2px {
    border-right: solid 2px;
}

  .bd-c_whiteAlpha\.500 {
    border-color: var(--colors-white-alpha-500);
}

  .mx_8px {
    margin-inline: 8px;
}

  .flex_50\% {
    flex: 50%;
}

  .flex_1_1_50\% {
    flex: 1 1 50%;
}

  .flex_0_0_65px {
    flex: 0 0 65px;
}

  .border-l_1px_solid_\#ddd {
    border-left: 1px solid #ddd;
}

  .border-l_3px_solid {
    border-left: 3px solid;
}

  .bd-c_other\.dividerStroke {
    border-color: var(--colors-other-divider-stroke);
}

  .bd-w_0 {
    border-width: 0;
}

  .border-style_solid {
    border-style: solid;
}

  .flex_0_0_calc\(_33\.3333\%_-_10\.7px\) {
    flex: 0 0 calc( 33.3333% - 10.7px);
}

  .bd-c_primary\.light {
    border-color: var(--colors-primary-light);
}

  .trs_all_250ms_ease-out {
    transition: all 250ms ease-out;
}

  .bdr_6px {
    border-radius: 6px;
}

  .bdr_3 {
    border-radius: 3px;
}

  .columns_4 {
    columns: 4;
}

  .border-l_4px_solid {
    border-left: 4px solid;
}

  .bd-c_warningDark {
    border-color: var(--colors-warning-dark);
}

  .columns_7 {
    columns: 7;
}

  .trs_all_150ms_ease {
    transition: all 150ms ease;
}

  .trs_all_300ms_ease-out {
    transition: all 300ms ease-out;
}

  .mx_8 {
    margin-inline: var(--spacing-8);
}

  .trs_color_0\.2s {
    transition: color 0.2s;
}

  .trs_all_200ms_ease-in-out {
    transition: all 200ms ease-in-out;
}

  .border-t_2px_solid {
    border-top: 2px solid;
}

  .py_16 {
    padding-block: var(--spacing-16);
}

  .grid-r_1 {
    grid-row: 1;
}

  .grid-r_1_\/_3 {
    grid-row: 1 / 3;
}

  .grid-r_2 {
    grid-row: 2;
}

  .grid-c_2 {
    grid-column: 2;
}

  .py_10px {
    padding-block: 10px;
}

  .gap_10px {
    gap: 10px;
}

  .trs_background_0\.2s_ease-in-out {
    transition: background 0.2s ease-in-out;
}

  .bd-c_chakra-border-color {
    border-color: chakra-border-color;
}

  .px_4px {
    padding-inline: 4px;
}

  .flex_0_0_246px {
    flex: 0 0 246px;
}

  .place-self_end {
    place-self: end;
}

  .trs_all_500ms_ease-in-out {
    transition: all 500ms ease-in-out;
}

  .trs_opacity_0\.15s_ease {
    transition: opacity 0.15s ease;
}

  .bd-c_greys\.50 {
    border-color: var(--colors-greys-50);
}

  .border-r_1px_solid {
    border-right: 1px solid;
}

  .bd-c_initial {
    border-color: initial;
}

  .flex_stretch {
    flex: stretch;
}

  .flex_70\% {
    flex: 70%;
}

  .bd-c_success {
    border-color: var(--colors-success);
}

  .flex_40 {
    flex: 40;
}

  .flex_60 {
    flex: 60;
}

  .bdr_12px {
    border-radius: 12px;
}

  .bdr_10px {
    border-radius: 10px;
}

  .gap_30px {
    gap: 30px;
}

  .bdr_5px {
    border-radius: 5px;
}

  .trs_transform_0\.15s {
    transition: transform 0.15s;
}

  .columns_2 {
    columns: 2;
}

  .py_10 {
    padding-block: var(--spacing-10);
}

  .border-b_1px_solid_\#eaedef {
    border-bottom: 1px solid #eaedef;
}

  .border-l_\#2e96d0_4px_solid {
    border-left: #2e96d0 4px solid;
}

  .border-l_\#e0e0e0_4px_solid {
    border-left: #e0e0e0 4px solid;
}

  .border-b_solid_1px {
    border-bottom: solid 1px;
}

  .border-b_\#eeeeee_1px_solid {
    border-bottom: #eeeeee 1px solid;
}

  .bdr_none {
    border-radius: none;
}

  .bdr_0_1px_1px_0 {
    border-radius: 0 1px 1px 0;
}

  .bdr_0_20px_20px_0 {
    border-radius: 0 20px 20px 0;
}

  .bdr_20px_0_0_20px {
    border-radius: 20px 0 0 20px;
}

  .px_12 {
    padding-inline: var(--spacing-12);
}

  .trs_all_250ms_linear {
    transition: all 250ms linear;
}

  .bd-w_2 {
    border-width: 2px;
}

  .bg-p_top_left {
    background-position: top left;
}

  .trs_opacity_50ms_ease-in {
    transition: opacity 50ms ease-in;
}

  .bd-c_greys\.100 {
    border-color: var(--colors-greys-100);
}

  .bd-c_black {
    border-color: var(--colors-black);
}

  .bd-c_blackAlpha\.50 {
    border-color: var(--colors-black-alpha-50);
}

  .td_line-through {
    text-decoration: line-through;
}

  .bd-c_blackAlpha\.200 {
    border-color: var(--colors-black-alpha-200);
}

  .flex_1_0_33\.3333\% {
    flex: 1 0 33.3333%;
}

  .my_2px {
    margin-block: 2px;
}

  .trs_200ms_padding_ease-out {
    transition: 200ms padding ease-out;
}

  .border-b_1px_solid_\#ededed {
    border-bottom: 1px solid #ededed;
}

  .bdr_100px {
    border-radius: 100px;
}

  .mx_-4 {
    margin-inline: calc(var(--spacing-4) * -1);
}

  .bdr_10 {
    border-radius: 10px;
}

  .bdr_50 {
    border-radius: 50px;
}

  .border-l_1px_solid {
    border-left: 1px solid;
}

  .px_10 {
    padding-inline: var(--spacing-10);
}

  .bd-c_warning {
    border-color: var(--colors-warning);
}

  .bdr_20 {
    border-radius: 20px;
}

  .my_33\% {
    margin-block: 33%;
}

  .mx_3 {
    margin-inline: var(--spacing-3);
}

  .my_200px {
    margin-block: 200px;
}

  .my_12 {
    margin-block: var(--spacing-12);
}

  .bd-c_inherit {
    border-color: inherit;
}

  .my_5 {
    margin-block: var(--spacing-5);
}

  .trs_background-color_200ms {
    transition: background-color 200ms;
}

  .bd-c_card\.borderColor {
    border-color: var(--colors-card-border-color);
}

  .trs_background-color_0\.3s_ease {
    transition: background-color 0.3s ease;
}

  .bdr_0\.25rem_0_0_0\.25rem {
    border-radius: 0.25rem 0 0 0.25rem;
}

  .py_0\.1em {
    padding-block: 0.1em;
}

  .px_0\.5em {
    padding-inline: 0.5em;
}

  .flex_1_100\% {
    flex: 1 100%;
}

  .flex-flow_wrap {
    flex-flow: wrap;
}

  .flex-flow_nowrap {
    flex-flow: nowrap;
}

  .bd-c_warningLight {
    border-color: var(--colors-warning-light);
}

  .gap_5 {
    gap: var(--spacing-5);
}

  .trs_color_ease-in_100ms {
    transition: color ease-in 100ms;
}

  .trs_all_ease-in-out_200ms {
    transition: all ease-in-out 200ms;
}

  .border-l_5px_solid {
    border-left: 5px solid;
}

  .bd-c_primary\.200 {
    border-color: primary.200;
}

  .bdr_20px {
    border-radius: 20px;
}

  .columns_1 {
    columns: 1;
}

  .trs_none {
    transition: none;
}

  .bd-c_red\.500 {
    border-color: var(--colors-red-500);
}

  .cg_10 {
    column-gap: var(--spacing-10);
}

  .rg_10 {
    row-gap: var(--spacing-10);
}

  .position_relative {
    position: relative;
}

  .position_fixed {
    position: fixed;
}

  .position_absolute {
    position: absolute;
}

  .d_flex {
    display: flex;
}

  .flex-d_column {
    flex-direction: column;
}

  .ai_center {
    align-items: center;
}

  .float_right {
    float: right;
}

  .box_4 {
    width: var(--sizes-4);
    height: var(--sizes-4);
}

  .box_5 {
    width: var(--sizes-5);
    height: var(--sizes-5);
}

  .d_inline {
    display: inline;
}

  .fs_xl {
    font-size: var(--font-sizes-xl);
}

  .fs_md {
    font-size: var(--font-sizes-md);
}

  .flex-d_row {
    flex-direction: row;
}

  .bx-sh_-14px_0px_12px_-5px_white {
    box-shadow: -14px 0px 12px -5px white;
}

  .bg-c_\#ffbede {
    background-color: #ffbede;
}

  .fs_2xl {
    font-size: var(--font-sizes-2xl);
}

  .bd-be-w_var\(--thickness\) {
    border-block-end-width: var(--thickness);
}

  .d_grid {
    display: grid;
}

  .ai_flex-start {
    align-items: flex-start;
}

  .jc_space-between {
    justify-content: space-between;
}

  .as_stretch {
    align-self: stretch;
}

  .justify-self_stretch {
    justify-self: stretch;
}

  .box_6 {
    width: var(--sizes-6);
    height: var(--sizes-6);
}

  .fs_1\.1em {
    font-size: 1.1em;
}

  .fs_1\.15em {
    font-size: 1.15em;
}

  .fw_semibold {
    font-weight: var(--font-weights-semibold);
}

  .bkdp_blur\(8px\) {
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

  .bx-sh_glassShadow {
    box-shadow: var(--shadows-glass-shadow);
}

  .z_1 {
    z-index: 1;
}

  .filter_drop-shadow\(0px_1px_1px_rgba\(0\,_0\,_0\,_0\.10\)\)_drop-shadow\(0px_2px_2px_rgba\(0\,_0\,_0\,_0\.09\)\)_drop-shadow\(0px_5px_3px_rgba\(0\,_0\,_0\,_0\.05\)\)_drop-shadow\(0px_10px_4px_rgba\(0\,_0\,_0\,_0\.01\)\)_drop-shadow\(0px_15px_4px_rgba\(0\,_0\,_0\,_0\.00\)\) {
    filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.10)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.09)) drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 4px rgba(0, 0, 0, 0.01)) drop-shadow(0px 15px 4px rgba(0, 0, 0, 0.00));
}

  .box_66px\! {
    width: 66px !important;
    height: 66px !important;
}

  .jc_center {
    justify-content: center;
}

  .color_greys\.600 {
    color: var(--colors-greys-600);
}

  .items_center {
    align-items: center;
}

  .ta_center {
    text-align: center;
}

  .color_primary\.main {
    color: var(--colors-primary-main);
}

  .color-scheme_primary {
    color-scheme: primary;
}

  .fs_6xl {
    font-size: var(--font-sizes-6xl);
}

  .color_greys\.100 {
    color: var(--colors-greys-100);
}

  .pointer-events_none {
    pointer-events: none;
}

  .d_none {
    display: none;
}

  .bkdp_blur\(20px\) {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
}

  .ai_stretch {
    align-items: stretch;
}

  .ring-c_greys\.100 {
    outline-color: var(--colors-greys-100);
}

  .ring-o_-1px {
    outline-offset: -1px;
}

  .ai_start {
    align-items: start;
}

  .bd-e-w_var\(--thickness\) {
    border-inline-end-width: var(--thickness);
}

  .position_static {
    position: static;
}

  .lh_1\.4 {
    line-height: 1.4;
}

  .items_start {
    align-items: flex-start;
}

  .fs_sm {
    font-size: var(--font-sizes-sm);
}

  .ff_body {
    font-family: var(--fonts-body);
}

  .fw_normal {
    font-weight: var(--font-weights-normal);
}

  .ta_right {
    text-align: right;
}

  .cursor_text {
    cursor: text;
}

  .white-space_pre-wrap {
    white-space: pre-wrap;
}

  .bx-sh_md {
    box-shadow: var(--shadows-md);
}

  .cursor_pointer {
    cursor: pointer;
}

  .jc_flex-end {
    justify-content: flex-end;
}

  .fs_xs {
    font-size: var(--font-sizes-xs);
}

  .bx-sh_rgba\(0\,_0\,_0\,_0\.04\)_0px_0px_1px_0px\,_rgba\(0\,_0\,_0\,_0\.06\)_0px_0px_2px_0px\,_rgba\(0\,_0\,_0\,_0\.04\)_0px_4px_8px_0px {
    box-shadow: rgba(0, 0, 0, 0.04) 0px 0px 1px 0px, rgba(0, 0, 0, 0.06) 0px 0px 2px 0px, rgba(0, 0, 0, 0.04) 0px 4px 8px 0px;
}

  .grid-tr_min-content_auto_minmax\(0\,_1fr\) {
    grid-template-rows: min-content auto minmax(0, 1fr);
}

  .ac_start {
    align-content: start;
}

  .wb_break-all {
    word-break: break-all;
}

  .tov_ellipsis {
    text-overflow: ellipsis;
}

  .white-space_nowrap {
    white-space: nowrap;
}

  .color_greys\.400 {
    color: var(--colors-greys-400);
}

  .ta_end {
    text-align: end;
}

  .box_12px {
    width: 12px;
    height: 12px;
}

  .flex-sh_0 {
    flex-shrink: 0;
}

  .color-scheme_red {
    color-scheme: red;
}

  .z_0 {
    z-index: 0;
}

  .color_primary\.500 {
    color: var(--colors-primary-500);
}

  .color_textMuted {
    color: var(--colors-text-muted);
}

  .box_40px {
    width: 40px;
    height: 40px;
}

  .fs_lg {
    font-size: var(--font-sizes-lg);
}

  .color_red\.500 {
    color: var(--colors-red-500);
}

  .fw_light {
    font-weight: var(--font-weights-light);
}

  .font-style_italic {
    font-style: italic;
}

  .color_greys\.500 {
    color: var(--colors-greys-500);
}

  .white-space_break-spaces {
    white-space: break-spaces;
}

  .flex-g_1 {
    flex-grow: 1;
}

  .color_greys\.300 {
    color: var(--colors-greys-300);
}

  .bdr-b_lg {
    border-bottom-left-radius: var(--radii-lg);
    border-bottom-right-radius: var(--radii-lg);
}

  .d_inline-flex {
    display: inline-flex;
}

  .va_top {
    vertical-align: top;
}

  .lh_1 {
    line-height: 1;
}

  .color_white {
    color: var(--colors-white);
}

  .d_block {
    display: block;
}

  .color_currentColor {
    color: currentColor;
}

  .color_black {
    color: var(--colors-black);
}

  .flex-dir_right {
    flex-direction: right;
}

  .bx-sh_inputOutline {
    box-shadow: var(--shadows-input-outline);
}

  .trs-prop_border-color\,_box-shadow\,_background-color {
    --transition-prop: border-color, box-shadow, background-color;
    transition-property: border-color, box-shadow, background-color;
}

  .pointer-events_auto {
    pointer-events: auto;
}

  .ai_flex-end {
    align-items: flex-end;
}

  .lh_0 {
    line-height: 0;
}

  .cursor_pinter {
    cursor: pinter;
}

  .fw_500 {
    font-weight: 500;
}

  .color-scheme_segments\.primary {
    color-scheme: segments.primary;
}

  .color_blackAlpha\.500 {
    color: var(--colors-black-alpha-500);
}

  .box_20px {
    width: 20px;
    height: 20px;
}

  .trf_translate\(20\%\,_-20\%\) {
    transform: translate(20%, -20%);
}

  .bx-sh_base {
    box-shadow: var(--shadows-base);
}

  .bx-sh_2xl {
    box-shadow: var(--shadows-2xl);
}

  .z_4 {
    z-index: 4;
}

  .op_0 {
    opacity: 0;
}

  .op_1 {
    opacity: 1;
}

  .box_1em {
    width: 1em;
    height: 1em;
}

  .as_flex-start {
    align-self: flex-start;
}

  .d_inline-block {
    display: inline-block;
}

  .box_8 {
    width: var(--sizes-8);
    height: var(--sizes-8);
}

  .box_10px {
    width: 10px;
    height: 10px;
}

  .fw_medium {
    font-weight: var(--font-weights-medium);
}

  .box_1\.25em {
    width: 1.25em;
    height: 1.25em;
}

  .jc_flex-start {
    justify-content: flex-start;
}

  .ta_left {
    text-align: left;
}

  .white-space_normal {
    white-space: normal;
}

  .box_0\.75rem {
    width: 0.75rem;
    height: 0.75rem;
}

  .items_flex-start {
    align-items: flex-start;
}

  .lh_shorter {
    line-height: shorter;
}

  .fs_70px {
    font-size: 70px;
}

  .fs_4xl {
    font-size: var(--font-sizes-4xl);
}

  .ai_baseline {
    align-items: baseline;
}

  .tt_capitalize {
    text-transform: capitalize;
}

  .wb_break-word {
    word-break: break-word;
}

  .ov-wrap_anywhere {
    overflow-wrap: anywhere;
}

  .fs_3xl {
    font-size: var(--font-sizes-3xl);
}

  .trf_translate\(-50\%\,_0\) {
    transform: translate(-50%, 0);
}

  .trf_translate\(-50\%\,_100px\) {
    transform: translate(-50%, 100px);
}

  .fs_8px {
    font-size: 8px;
}

  .scr-bar_hidden {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

  .scr-bar_hidden::-webkit-scrollbar {
    display: none;
}

  .as_flex-end {
    align-self: flex-end;
}

  .items_stretch {
    align-items: stretch;
}

  .color_text {
    color: var(--colors-text);
}

  .ls_1px {
    letter-spacing: 1px;
}

  .tt_uppercase {
    text-transform: uppercase;
}

  .bx-sh_lg {
    box-shadow: var(--shadows-lg);
}

  .ff_Rubik\,_sans-serif {
    font-family: Rubik, sans-serif;
}

  .flex-g_1_0_300px {
    flex-grow: 1 0 300px;
}

  .white-space_pre-line {
    white-space: pre-line;
}

  .box_14px {
    width: 14px;
    height: 14px;
}

  .items_end {
    align-items: flex-end;
}

  .lh_34px {
    line-height: 34px;
}

  .items_top {
    align-items: top;
}

  .fs_9px {
    font-size: 9px;
}

  .flex-b_50\% {
    flex-basis: 50%;
}

  .bx-sh_xl {
    box-shadow: var(--shadows-xl);
}

  .fill_workspace\.sidebar\.text {
    fill: var(--colors-workspace-sidebar-text);
}

  .filter_drop-shadow\(0px_1px_1px_rgba\(35\,_27\,_27\,_0\.1\)\)_drop-shadow\(0px_2px_2px_rgba\(0\,_0\,_0\,_0\.09\)\)_drop-shadow\(0px_5px_3px_rgba\(0\,_0\,_0\,_0\.05\)\)_drop-shadow\(0px_10px_4px_rgba\(0\,_0\,_0\,_0\.01\)\)_drop-shadow\(0px_15px_4px_rgba\(0\,_0\,_0\,_0\.00\)\) {
    filter: drop-shadow(0px 1px 1px rgba(35, 27, 27, 0.1)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.09)) drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 4px rgba(0, 0, 0, 0.01)) drop-shadow(0px 15px 4px rgba(0, 0, 0, 0.00));
}

  .d_inherit {
    display: inherit;
}

  .fs_16px {
    font-size: 16px;
}

  .trf-o_50\%_50\% {
    transform-origin: 50% 50%;
}

  .ai_end {
    align-items: end;
}

  .lh_initial {
    line-height: initial;
}

  .color-scheme_secondary {
    color-scheme: secondary;
}

  .bx-sh_none {
    box-shadow: none;
}

  .items_baseline {
    align-items: baseline;
}

  .trf_translateY\(4px\) {
    transform: translateY(4px);
}

  .bg-c_greys\.50 {
    background-color: var(--colors-greys-50);
}

  .flex-d_row-reverse {
    flex-direction: row-reverse;
}

  .trf_scale\(2\) {
    transform: scale(2);
}

  .trf_scale\(1\) {
    transform: scale(1);
}

  .lh_1\.2 {
    line-height: 1.2;
}

  .fw_regular {
    font-weight: regular;
}

  .ai_right {
    align-items: right;
}

  .position_sticky {
    position: sticky;
}

  .topics_Details_not_available\. {
    topics: Details not available.;
}

  .body_Details_not_available\. {
    body: Details not available.;
}

  .title_Key_topic {
    title: Key topic;
}

  .sub-title_Detailed_root_cause {
    sub-title: Detailed root cause;
}

  .grid-rg_0 {
    grid-row-gap: var(--spacing-0);
}

  .grid-rg_1 {
    grid-row-gap: var(--spacing-1);
}

  .fill_primary\.500 {
    fill: var(--colors-primary-500);
}

  .fill_primary\.300 {
    fill: var(--colors-primary-300);
}

  .bg-c_greys\.100 {
    background-color: var(--colors-greys-100);
}

  .bg-c_primary\.50 {
    background-color: primary.50;
}

  .cursor_default {
    cursor: default;
}

  .bg-c_greys\.white {
    background-color: var(--colors-greys-white);
}

  .flex-dir_rtl {
    flex-direction: rtl;
}

  .box_2 {
    width: var(--sizes-2);
    height: var(--sizes-2);
}

  .cursor_cursor {
    cursor: cursor;
}

  .color_inherit {
    color: inherit;
}

  .cg_4 {
    column-gap: var(--spacing-4);
}

  .rg_2 {
    row-gap: var(--spacing-2);
}

  .tsh_0px_4px_10px_rgba\(66\,_66\,_66\,_0\.2\) {
    text-shadow: 0px 4px 10px rgba(66, 66, 66, 0.2);
}

  .fw_600 {
    font-weight: 600;
}

  .op_0\.4 {
    opacity: 0.4;
}

  .us_none {
    -webkit-user-select: none;
    user-select: none;
}

  .scr-bhv_inside {
    scroll-behavior: inside;
}

  .color_danger {
    color: danger;
}

  .bx-sh_1px_0_2px_0px_rgba\(0\,0\,0\,0\.3\) {
    box-shadow: 1px 0 2px 0px rgba(0,0,0,0.3);
}

  .trf_translateX\(-50\%\) {
    transform: translateX(-50%);
}

  .bx-sh_2px_2px_2px_0_rgba\(0\,0\,0\,0\.2\) {
    box-shadow: 2px 2px 2px 0 rgba(0,0,0,0.2);
}

  .color_green\.400 {
    color: var(--colors-green-400);
}

  .trf_translateY\(-100\%\) {
    transform: translateY(-100%);
}

  .color_signal\.positive {
    color: var(--colors-signal-positive);
}

  .color_signal\.caution {
    color: var(--colors-signal-caution);
}

  .color_signal\.negative {
    color: var(--colors-signal-negative);
}

  .bg-c_white {
    background-color: var(--colors-white);
}

  .box_18px {
    width: 18px;
    height: 18px;
}

  .jc_end {
    justify-content: end;
}

  .bdr-l_none {
    border-top-left-radius: none;
    border-bottom-left-radius: none;
}

  .bdr-l_4px {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
}

  .bdr-r_none {
    border-top-right-radius: none;
    border-bottom-right-radius: none;
}

  .bdr-r_4px {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
}

  .trs-dur_unset {
    --transition-duration: unset;
    transition-duration: unset;
}

  .bg-r_no-repeat\! {
    background-repeat: no-repeat !important;
}

  .bg-s_100\%_auto\! {
    background-size: 100% auto !important;
}

  .fill_greys\.600 {
    fill: var(--colors-greys-600);
}

  .flex-wrap_nowrap {
    flex-wrap: nowrap;
}

  .color_brand\.primary {
    color: var(--colors-brand-primary);
}

  .color-scheme_danger {
    color-scheme: danger;
}

  .items_flex-end {
    align-items: flex-end;
}

  .bx-sh_0px_-4px_8px_-6px_rgba\(0\,_0\,_0\,_0\.20\) {
    box-shadow: 0px -4px 8px -6px rgba(0, 0, 0, 0.20);
}

  .bdr-t_md {
    border-top-left-radius: var(--radii-md);
    border-top-right-radius: var(--radii-md);
}

  .bx-sh_extended {
    box-shadow: var(--shadows-extended);
}

  .bx-sh_0_2px_0_0_\#E0E0E0\,_0_2px_4px_0_rgba\(0\,_0\,_0\,_0\.06\)_inset {
    box-shadow: 0 2px 0 0 #E0E0E0, 0 2px 4px 0 rgba(0, 0, 0, 0.06) inset;
}

  .bdr-r_0 {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

  .bdr-l_0 {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

  .bg-c_\#fff {
    background-color: #fff;
}

  .bx-sh_0px_1px_2px_0px_rgba\(0\,_0\,_0\,_0\.1\) {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
}

  .color_greys\.200 {
    color: var(--colors-greys-200);
}

  .z_1000 {
    z-index: 1000;
}

  .ff_mono {
    font-family: var(--fonts-mono);
}

  .va_middle {
    vertical-align: middle;
}

  .bg-c_gray\.200 {
    background-color: var(--colors-gray-200);
}

  .tsh_0px_4px_10px_\#42424280 {
    text-shadow: 0px 4px 10px #42424280;
}

  .fs_medium {
    font-size: medium;
}

  .fs_14px {
    font-size: 14px;
}

  .box_24 {
    width: var(--sizes-24);
    height: var(--sizes-24);
}

  .fw_bold {
    font-weight: var(--font-weights-bold);
}

  .trf_rotate\(90deg\) {
    transform: rotate(90deg);
}

  .trf_rotate\(0deg\) {
    transform: rotate(0deg);
}

  .vis_visible {
    visibility: visible;
}

  .vis_hidden {
    visibility: hidden;
}

  .op_100 {
    opacity: 100;
}

  .filter_drop-shadow\(0px_-4px_8px_rgba\(0\,_0\,_0\,_0\.04\)\)_drop-shadow\(0px_0px_2px_rgba\(0\,_0\,_0\,_0\.06\)\)_drop-shadow\(0px_0px_1px_rgba\(0\,_0\,_0\,_0\.04\)\) {
    filter: drop-shadow(0px -4px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.06)) drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04));
}

  .jc_start {
    justify-content: start;
}

  .float_left {
    float: left;
}

  .bg-c_primary\.500 {
    background-color: var(--colors-primary-500);
}

  .tsh_textExtended {
    text-shadow: var(--shadows-text-extended);
}

  .fs_1\.75rem {
    font-size: 1.75rem;
}

  .grid-cg_2 {
    grid-column-gap: var(--spacing-2);
}

  .grid-rg_4 {
    grid-row-gap: var(--spacing-4);
}

  .color_warning {
    color: var(--colors-warning);
}

  .lh_5 {
    line-height: 5;
}

  .flex-g_0 {
    flex-grow: 0;
}

  .flex-sh_1 {
    flex-shrink: 1;
}

  .box_20px\! {
    width: 20px !important;
    height: 20px !important;
}

  .cx_50\% {
    cx: 50%;
}

  .cy_50\% {
    cy: 50%;
}

  .stk_white {
    stroke: var(--colors-white);
}

  .stk-w_2 {
    stroke-width: 2;
}

  .anim-dur_300 {
    animation-duration: 300px;
}

  .trf_translate\(-50\%\,_-50\%\) {
    transform: translate(-50%, -50%);
}

  .lh_6 {
    line-height: 6;
}

  .lh_10 {
    line-height: 10;
}

  .fs_5xl {
    font-size: var(--font-sizes-5xl);
}

  .op_0\.2 {
    opacity: 0.2;
}

  .box_12px\! {
    width: 12px !important;
    height: 12px !important;
}

  .fs_2xs {
    font-size: var(--font-sizes-2xs);
}

  .flex-dir_row {
    flex-direction: row;
}

  .filter_drop-shadow\(0px_1px_2px_rgba\(0\,_0\,_0\,_0\.2\)\) {
    filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.2));
}

  .color_text\.light {
    color: text.light;
}

  .fs_14 {
    font-size: 14px;
}

  .cg_2 {
    column-gap: var(--spacing-2);
}

  .fs_1rem {
    font-size: 1rem;
}

  .jc_initial {
    justify-content: initial;
}

  .lh_1\.25 {
    line-height: 1.25;
}

  .fs_small {
    font-size: small;
}

  .color_\#816091 {
    color: #816091;
}

  .ta_start {
    text-align: start;
}

  .trf_scale\(1\,_1\) {
    transform: scale(1, 1);
}

  .trf_scale\(1\,_-1\) {
    transform: scale(1, -1);
}

  .fs_20px {
    font-size: 20px;
}

  .bg-grad_linear\(172deg\,_rgba\(244\,_255\,_5\,_0\)_19\.58\%\,_rgba\(253\,_99\,_210\,_0\.032\)_46\.97\%\,_rgba\(56\,_255\,_231\,_0\)_84\.06\%\) {
    background-image: linear(172deg, rgba(244, 255, 5, 0) 19.58%, rgba(253, 99, 210, 0.032) 46.97%, rgba(56, 255, 231, 0) 84.06%);
}

  .color_gold {
    color: gold;
}

  .fs_0\.8em {
    font-size: 0.8em;
}

  .z_1400 {
    z-index: 1400;
}

  .bdr-b_8px {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

  .bdr-t_2px {
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
}

  .bx-sh_0_-4px_8px_-4px_rgba\(0\,0\,0\,0\.2\) {
    box-shadow: 0 -4px 8px -4px rgba(0,0,0,0.2);
}

  .trf_translateY\(25\%\) {
    transform: translateY(25%);
}

  .jc_bottom {
    justify-content: bottom;
}

  .color_success {
    color: var(--colors-success);
}

  .items_right {
    align-items: right;
}

  .ai_top {
    align-items: top;
}

  .justify-items_start {
    justify-items: start;
}

  .justify-self_end {
    justify-self: end;
}

  .trf_translateY\(-50\%\) {
    transform: translateY(-50%);
}

  .fs_10px {
    font-size: 10px;
}

  .op_0\.6 {
    opacity: 0.6;
}

  .color-scheme_segments\.secondary {
    color-scheme: segments.secondary;
}

  .color-scheme_segments\.tertiary {
    color-scheme: segments.tertiary;
}

  .color-scheme_segments\.quaternary {
    color-scheme: segments.quaternary;
}

  .box_22px {
    width: 22px;
    height: 22px;
}

  .color_primary\.dark {
    color: var(--colors-primary-dark);
}

  .op_0\.8 {
    opacity: 0.8;
}

  .op_0\.5 {
    opacity: 0.5;
}

  .ls_0\.06em {
    letter-spacing: 0.06em;
}

  .duration_0\.5 {
    duration: 0.5px;
}

  .bg-c_greys\.300 {
    background-color: var(--colors-greys-300);
}

  .color-scheme_greys {
    color-scheme: greys;
}

  .trf_translateX\(50\%\) {
    transform: translateX(50%);
}

  .grid-rg_1px {
    grid-row-gap: 1px;
}

  .cursor_auto {
    cursor: auto;
}

  .lh_8 {
    line-height: 8;
}

  .ls_0\.03em {
    letter-spacing: 0.03em;
}

  .items_left {
    align-items: left;
}

  .trf_rotate\(180deg\) {
    transform: rotate(180deg);
}

  .flex-wrap_initial {
    flex-wrap: initial;
}

  .flex-dir_ltr {
    flex-direction: ltr;
}

  .trf_translate\(-50\%\,_100\%\) {
    transform: translate(-50%, 100%);
}

  .box_0\.75em {
    width: 0.75em;
    height: 0.75em;
}

  .trf_translateY\(1px\) {
    transform: translateY(1px);
}

  .jc_space-around {
    justify-content: space-around;
}

  .color-scheme_graph\.primary {
    color-scheme: graph.primary;
}

  .bx-sh_0_-4px_8px_-2px_rgba\(0\,0\,0\,0\.1\) {
    box-shadow: 0 -4px 8px -2px rgba(0,0,0,0.1);
}

  .flex-wrap_wrap {
    flex-wrap: wrap;
}

  .font-style_normal {
    font-style: normal;
}

  .box_8px {
    width: 8px;
    height: 8px;
}

  .z_2 {
    z-index: 2;
}

  .cursor_not-allowed {
    cursor: not-allowed;
}

  .box_16px {
    width: 16px;
    height: 16px;
}

  .box_3 {
    width: var(--sizes-3);
    height: var(--sizes-3);
}

  .resize_none {
    resize: none;
}

  .color-scheme_gray {
    color-scheme: gray;
}

  .bx-sh_0px_16px_24px_0px_rgba\(0\,_0\,_0\,_0\.06\)\,_0px_2px_6px_0px_rgba\(0\,_0\,_0\,_0\.04\)\,_0px_0px_1px_0px_rgba\(0\,_0\,_0\,_0\.04\) {
    box-shadow: 0px 16px 24px 0px rgba(0, 0, 0, 0.06), 0px 2px 6px 0px rgba(0, 0, 0, 0.04), 0px 0px 1px 0px rgba(0, 0, 0, 0.04);
}

  .bg-c_signal\.positive {
    background-color: var(--colors-signal-positive);
}

  .bg-c_segments\.senary\.500 {
    background-color: var(--colors-segments-senary-500);
}

  .items_self-start {
    align-items: self-start;
}

  .fs_24px {
    font-size: 24px;
}

  .bx-sh_1px_1px_2px_0px_rgba\(255\,_255\,_255\,_0\.30\)_inset\,_-1px_-1px_2px_0px_rgba\(223\,_223\,_223\,_0\.50\)_inset\,_-5px_5px_10px_0px_rgba\(223\,_223\,_223\,_0\.20\)\,_5px_-5px_10px_0px_rgba\(223\,_223\,_223\,_0\.20\)\,_-5px_-5px_10px_0px_rgba\(255\,_255\,_255\,_0\.90\)\,_5px_5px_13px_0px_rgba\(223\,_223\,_223\,_0\.90\) {
    box-shadow: 1px 1px 2px 0px rgba(255, 255, 255, 0.30) inset, -1px -1px 2px 0px rgba(223, 223, 223, 0.50) inset, -5px 5px 10px 0px rgba(223, 223, 223, 0.20), 5px -5px 10px 0px rgba(223, 223, 223, 0.20), -5px -5px 10px 0px rgba(255, 255, 255, 0.90), 5px 5px 13px 0px rgba(223, 223, 223, 0.90);
}

  .bg-c_primary\.main {
    background-color: var(--colors-primary-main);
}

  .bg-c_transparent {
    background-color: var(--colors-transparent);
}

  .fs_18px {
    font-size: 18px;
}

  .bg-cp_text {
    background-clip: text;
    -webkit-background-clip: text;
}

  .color_transparent {
    color: var(--colors-transparent);
}

  .bg-c_segmentPrimary {
    background-color: var(--colors-segment-primary);
}

  .grid-af_column {
    grid-auto-flow: column;
}

  .grid-ac_1fr {
    grid-auto-columns: 1fr;
}

  .color_gray\.600 {
    color: var(--colors-gray-600);
}

  .lh_1\.5 {
    line-height: 1.5;
}

  .color-scheme_green {
    color-scheme: green;
}

  .fs_15px {
    font-size: 15px;
}

  .bx-sh_0_-3px_7px_-5px_\#757575 {
    box-shadow: 0 -3px 7px -5px #757575;
}

  .box_24px {
    width: 24px;
    height: 24px;
}

  .color_warningDark {
    color: var(--colors-warning-dark);
}

  .fs_1\.2rem {
    font-size: 1.2rem;
}

  .color_greys {
    color: greys;
}

  .isolation_isolate {
    isolation: isolate;
}

  .bx-sh_dark-lg {
    box-shadow: var(--shadows-dark-lg);
}

  .bx-sh_sm {
    box-shadow: var(--shadows-sm);
}

  .ac_center {
    align-content: center;
}

  .jc_right {
    justify-content: right;
}

  .bkdp-blur_2 {
    --backdrop-blur: blur(2);
}

  .bg-c_rgba\(255\,_255\,_255\,_0\.7\) {
    background-color: rgba(255, 255, 255, 0.7);
}

  .lh_20px {
    line-height: 20px;
}

  .box_16 {
    width: var(--sizes-16);
    height: var(--sizes-16);
}

  .fs_0\.875rem {
    font-size: 0.875rem;
}

  .color_blackAlpha\.400 {
    color: var(--colors-black-alpha-400);
}

  .lh_1\.8 {
    line-height: 1.8;
}

  .trf_scale\(1\,_2\) {
    transform: scale(1, 2);
}

  .color_red {
    color: red;
}

  .ms_0 {
    margin-inline-start: var(--spacing-0);
}

  .cg_8 {
    column-gap: var(--spacing-8);
}

  .filter_drop-shadow\(0px_-4px_8px_rgba\(0\,_0\,_0\,_0\.04\)\)_drop-shadow\(0px_0px_2px_rgba\(0\,_0\,_0\,_0\.06\)\)_drop-shadow\(0px_0px_1px_rgba\(0\,_0\,_0\,_0\.04\)\)\; {
    filter: drop-shadow(0px -4px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.06)) drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.04));;
}

  .as_end {
    align-self: end;
}

  .sub-title_Call_reason_description {
    sub-title: Call reason description;
}

  .bg-r_repeat-x {
    background-repeat: repeat-x;
}

  .bg-s_6px_2px {
    background-size: 6px 2px;
}

  .bg-i_radial-gradient\(circle\,_\#E0E0E0_1px\,_transparent_1px\) {
    background-image: radial-gradient(circle, #E0E0E0 1px, transparent 1px);
}

  .color_initial {
    color: initial;
}

  .ls_0\.05em {
    letter-spacing: 0.05em;
}

  .ac_flex-start {
    align-content: flex-start;
}

  .color_yellow\.500 {
    color: var(--colors-yellow-500);
}

  .color_orange\.700 {
    color: var(--colors-orange-700);
}

  .fw_initial {
    font-weight: initial;
}

  .color_gray\.400 {
    color: var(--colors-gray-400);
}

  .box_32px {
    width: 32px;
    height: 32px;
}

  .overlay_true {
    overlay: true;
}

  .ac_stretch {
    align-content: stretch;
}

  .ff_monospace {
    font-family: monospace;
}

  .flex-dir_1 {
    flex-direction: 1px;
}

  .tt_none {
    text-transform: none;
}

  .ls_normal {
    letter-spacing: var(--letter-spacings-normal);
}

  .trf_translateY\(-30px\) {
    transform: translateY(-30px);
}

  .fs_organizationNamexl {
    font-size: organizationNamexl;
}

  .cx_0 {
    cx: 0;
}

  .anim-dur_500 {
    animation-duration: 500px;
}

  .filter_url\(\#shadow\) {
    filter: url(#shadow);
}

  .stk-w_1 {
    stroke-width: 1;
}

  .position_insideBottomLeft {
    position: insideBottomLeft;
}

  .fs_10 {
    font-size: 10px;
}

  .stk-dsh_4 {
    stroke-dasharray: 4;
}

  .fs_2rem {
    font-size: 2rem;
}

  .fill_greys\.400 {
    fill: var(--colors-greys-400);
}

  .us_all {
    -webkit-user-select: all;
    user-select: all;
}

  .color_primary {
    color: primary;
}

  .flex-b_80px {
    flex-basis: 80px;
}

  .flex-b_250px {
    flex-basis: 250px;
}

  .box_9 {
    width: var(--sizes-9);
    height: var(--sizes-9);
}

  .white-space_pre {
    white-space: pre;
}

  .ms_0rem\! {
    margin-inline-start: 0rem !important;
}

  .cg_3 {
    column-gap: var(--spacing-3);
}

  .color_red\.800 {
    color: var(--colors-red-800);
}

  .color_GrayText {
    color: GrayText;
}

  .grid-tc_fit-content\(40\%\)_fit-content\(100\%\)_fit-content\(40\%\) {
    grid-template-columns: fit-content(40%) fit-content(100%) fit-content(40%);
}

  .lh_short {
    line-height: short;
}

  .color-scheme_pink {
    color-scheme: pink;
}

  .color_\#e81111 {
    color: #e81111;
}

  .color_\#ffc107 {
    color: #ffc107;
}

  .fs_large {
    font-size: large;
}

  .color_\#4b4b4b {
    color: #4b4b4b;
}

  .color_\#7f7f7f {
    color: #7f7f7f;
}

  .bdr-l_lg {
    border-top-left-radius: var(--radii-lg);
    border-bottom-left-radius: var(--radii-lg);
}

  .bdr-r_lg {
    border-top-right-radius: var(--radii-lg);
    border-bottom-right-radius: var(--radii-lg);
}

  .color_red\.900 {
    color: var(--colors-red-900);
}

  .color_green\.900 {
    color: var(--colors-green-900);
}

  .color-scheme_cyan {
    color-scheme: cyan;
}

  .flex-g_2 {
    flex-grow: 2;
}

  .bg-c_\#25748b {
    background-color: #25748b;
}

  .color_red\.600 {
    color: var(--colors-red-600);
}

  .color-scheme_orange {
    color-scheme: orange;
}

  .flex-d_column-reverse {
    flex-direction: column-reverse;
}

  .bx-sh_0_-4px_8px_-6px_rgba\(0\,0\,0\,0\.2\) {
    box-shadow: 0 -4px 8px -6px rgba(0,0,0,0.2);
}

  .ff_heading {
    font-family: var(--fonts-heading);
}

  .trf_translateY\(8px\) {
    transform: translateY(8px);
}

  .trf_translate\(0\,_-50\%\) {
    transform: translate(0, -50%);
}

  .bg-r_repeat {
    background-repeat: repeat;
}

  .cursor_move {
    cursor: move;
}

  .fill_greys\.200 {
    fill: var(--colors-greys-200);
}

  .ai_inherit {
    align-items: inherit;
}

  .fs_0\.75em {
    font-size: 0.75em;
}

  .lh_1\.1 {
    line-height: 1.1;
}

  .fs_0\.875em {
    font-size: 0.875em;
}

  .fs_1em {
    font-size: 1em;
}

  .fs_0\.65em {
    font-size: 0.65em;
}

  .fw_400 {
    font-weight: 400;
}

  .color_blackAlpha\.300 {
    color: var(--colors-black-alpha-300);
}

  .color_InfoText {
    color: InfoText;
}

  .scr-bhv_smooth {
    scroll-behavior: smooth;
}

  .trf_translateX\(16px\) {
    transform: translateX(16px);
}

  .color_primary\.300 {
    color: var(--colors-primary-300);
}

  .color_info {
    color: var(--colors-info);
}

  .box_3\! {
    width: var(--sizes-3) !important;
    height: var(--sizes-3) !important;
}

  .flex-dir_column {
    flex-direction: column;
}

  .color-scheme_blue {
    color-scheme: blue;
}

  .color_orange {
    color: orange;
}

  .fw_extrabold {
    font-weight: var(--font-weights-extrabold);
}

  .color_gray\.500 {
    color: var(--colors-gray-500);
}

  .bdr-ee_0 {
    border-end-end-radius: 0;
}

  .bdr-es_0 {
    border-end-start-radius: 0;
}

  .position_initial {
    position: initial;
}

  .anim-dur_1600 {
    animation-duration: 1600px;
}

  .fill_url\(\#gradient\) {
    fill: url(#gradient);
}

  .anim-dur_800 {
    animation-duration: 800px;
}

  .fs_0\.2em {
    font-size: 0.2em;
}

  .fs_0\.5em {
    font-size: 0.5em;
}

  .stk-w_1\.5 {
    stroke-width: 1.5;
}

  .box_16px\! {
    width: 16px !important;
    height: 16px !important;
}

  .bg-c_brand\.primary {
    background-color: var(--colors-brand-primary);
}

  .cg_1 {
    column-gap: var(--spacing-1);
}

  .fs_1\.5rem {
    font-size: 1.5rem;
}

  .color_subtitle {
    color: var(--colors-subtitle);
}

  .color_green {
    color: green;
}

  .color_primary\.400 {
    color: primary.400;
}

  .color_\#2F96D0 {
    color: #2F96D0;
}

  .color-scheme_teal {
    color-scheme: teal;
}

  .color_card\.header\.title {
    color: var(--colors-card-header-title);
}

  .color_card\.header\.subtitle {
    color: var(--colors-card-header-subtitle);
}

  .z_100 {
    z-index: 100;
}

  .as_center {
    align-self: center;
}

  .lh_inherit {
    line-height: inherit;
}

  .sr_true {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

  .li-t_none {
    list-style-type: none;
}

  .li-img_initial {
    list-style-image: initial;
}

  .li-pos_initial {
    list-style-position: initial;
}

  .z_auto {
    z-index: auto;
}

  .pointer-events_initial {
    pointer-events: initial;
}

  .bg-c_whiteAlpha\.500 {
    background-color: var(--colors-white-alpha-500);
}

  .d_contents {
    display: contents;
}

  .color_whiteAlpha\.500 {
    color: var(--colors-white-alpha-500);
}

  .color_whiteAlpha\.800 {
    color: var(--colors-white-alpha-800);
}

  .rg_4 {
    row-gap: var(--spacing-4);
}

  .fs_0\.9em {
    font-size: 0.9em;
}

  .va_baseline {
    vertical-align: baseline;
}

  .fill_orangered {
    fill: orangered;
}

  .fill_none {
    fill: none;
}

  .stk_none {
    stroke: none;
}

  .filter_url\(\#inset-shadow-filter\) {
    filter: url(#inset-shadow-filter);
}

  .stk_rgba\(255\,255\,255\,0\.7\) {
    stroke: rgba(255,255,255,0.7);
}

  .d_table-cell {
    display: table-cell;
}

  .ls_0 {
    letter-spacing: 0;
}

  .ls_0\.02em {
    letter-spacing: 0.02em;
}

  .fs_11px {
    font-size: 11px;
}

  .color_\#df3f3f {
    color: #df3f3f;
}

  .fs_x-large {
    font-size: x-large;
}

  .color-scheme_blackAlpha {
    color-scheme: blackAlpha;
}

  .box_1\.2em {
    width: 1.2em;
    height: 1.2em;
}

  .box_1\.25rem {
    width: 1.25rem;
    height: 1.25rem;
}

  .box_0\.875rem\! {
    width: 0.875rem !important;
    height: 0.875rem !important;
}

  .stk-w_1px {
    stroke-width: 1px;
}

  .fw_md {
    font-weight: md;
}

  .w_100vw {
    width: 100vw;
}

  .h_100vh {
    height: 100vh;
}

  .pl_2 {
    padding-left: var(--spacing-2);
}

  .pr_2 {
    padding-right: var(--spacing-2);
}

  .pt_2 {
    padding-top: var(--spacing-2);
}

  .pt_1 {
    padding-top: var(--spacing-1);
}

  .top_2 {
    top: var(--spacing-2);
}

  .right_2 {
    right: var(--spacing-2);
}

  .w_100\% {
    width: 100%;
}

  .h_100\% {
    height: 100%;
}

  .h_8 {
    height: var(--sizes-8);
}

  .w_auto {
    width: auto;
}

  .mt_20\% {
    margin-top: 20%;
}

  .pr_0 {
    padding-right: var(--spacing-0);
}

  .pb_2 {
    padding-bottom: var(--spacing-2);
}

  .max-h_660px {
    max-height: 660px;
}

  .mt_20 {
    margin-top: var(--spacing-20);
}

  .mb_2 {
    margin-bottom: var(--spacing-2);
}

  .mb_1 {
    margin-bottom: var(--spacing-1);
}

  .mt_4 {
    margin-top: var(--spacing-4);
}

  .w_52px {
    width: 52px;
}

  .w_52 {
    width: var(--sizes-52);
}

  .h_52 {
    height: var(--sizes-52);
}

  .w_90 {
    width: 90px;
}

  .h_24 {
    height: var(--sizes-24);
}

  .max-w_565px {
    max-width: 565px;
}

  .w_70\% {
    width: 70%;
}

  .w_30\% {
    width: 30%;
}

  .bottom_0 {
    bottom: var(--spacing-0);
}

  .top_0 {
    top: var(--spacing-0);
}

  .left_0 {
    left: var(--spacing-0);
}

  .right_0 {
    right: var(--spacing-0);
}

  .w_20vw {
    width: 20vw;
}

  .max-w_400px {
    max-width: 400px;
}

  .mb_8 {
    margin-bottom: var(--spacing-8);
}

  .w_90px {
    width: 90px;
}

  .pt_8 {
    padding-top: var(--spacing-8);
}

  .pb_8 {
    padding-bottom: var(--spacing-8);
}

  .ov-y_scroll {
    overflow-y: scroll;
}

  .min-h_650px {
    min-height: 650px;
}

  .w_200px {
    width: 200px;
}

  .max-w_30em {
    max-width: 30em;
}

  .mt_6vh {
    margin-top: 6vh;
}

  .mt_3 {
    margin-top: var(--spacing-3);
}

  .min-w_375px {
    min-width: 375px;
}

  .max-w_1600px {
    max-width: 1600px;
}

  .min-h_100vh {
    min-height: 100vh;
}

  .pr_8 {
    padding-right: var(--spacing-8);
}

  .pl_12 {
    padding-left: var(--spacing-12);
}

  .bd-l-w_2px {
    border-left-width: 2px;
}

  .bd-r-w_2px {
    border-right-width: 2px;
}

  .border-left-style_solid {
    border-left-style: solid;
}

  .border-right-style_solid {
    border-right-style: solid;
}

  .bd-l-c_workspace\.contentBorder {
    border-left-color: var(--colors-workspace-content-border);
}

  .bd-r-c_workspace\.contentBorder {
    border-right-color: var(--colors-workspace-content-border);
}

  .min-w_0 {
    min-width: var(--sizes-0);
}

  .pb_0 {
    padding-bottom: var(--spacing-0);
}

  .ov-y_auto {
    overflow-y: auto;
}

  .h_10 {
    height: var(--sizes-10);
}

  .mt_1 {
    margin-top: var(--spacing-1);
}

  .bd-b-c_greys\.100 {
    border-bottom-color: var(--colors-greys-100);
}

  .pl_3 {
    padding-left: var(--spacing-3);
}

  .pr_10 {
    padding-right: var(--spacing-10);
}

  .mt_2 {
    margin-top: var(--spacing-2);
}

  .h_full {
    height: var(--sizes-full);
}

  .ov-x_hidden {
    overflow-x: hidden;
}

  .h_55px {
    height: 55px;
}

  .pl_4 {
    padding-left: var(--spacing-4);
}

  .pb_3 {
    padding-bottom: var(--spacing-3);
}

  .bdr-tr_0px {
    border-top-right-radius: 0px;
}

  .bdr-tl_0px {
    border-top-left-radius: 0px;
}

  .bdr-bl_4px {
    border-bottom-left-radius: 4px;
}

  .bdr-br_4px {
    border-bottom-right-radius: 4px;
}

  .ml_0\.5rem {
    margin-left: 0.5rem;
}

  .pr_0\.75rem {
    padding-right: 0.75rem;
}

  .pr_1 {
    padding-right: var(--spacing-1);
}

  .bdr-tl_4px {
    border-top-left-radius: 4px;
}

  .bdr-tr_4px {
    border-top-right-radius: 4px;
}

  .pt_12px {
    padding-top: 12px;
}

  .w_full {
    width: var(--sizes-full);
}

  .max-w_140px {
    max-width: 140px;
}

  .min-w_110px {
    min-width: 110px;
}

  .min-h_3 {
    min-height: var(--sizes-3);
}

  .h_6 {
    height: var(--sizes-6);
}

  .min-w_300px {
    min-width: 300px;
}

  .ov-x_auto {
    overflow-x: auto;
}

  .mr_1 {
    margin-right: var(--spacing-1);
}

  .mr_2 {
    margin-right: var(--spacing-2);
}

  .ml_6 {
    margin-left: var(--spacing-6);
}

  .ml_2 {
    margin-left: var(--spacing-2);
}

  .h_200px {
    height: 200px;
}

  .mr_0 {
    margin-right: var(--spacing-0);
}

  .max-w_sm {
    max-width: var(--sizes-sm);
}

  .pb_2\.5 {
    padding-bottom: var(--spacing-2\.5);
}

  .pb_6 {
    padding-bottom: var(--spacing-6);
}

  .mb_10 {
    margin-bottom: var(--spacing-10);
}

  .h_14px {
    height: 14px;
}

  .mr_4 {
    margin-right: var(--spacing-4);
}

  .ml_0 {
    margin-left: var(--spacing-0);
}

  .min-h_5 {
    min-height: var(--sizes-5);
}

  .min-w_5 {
    min-width: var(--sizes-5);
}

  .max-w_100\% {
    max-width: 100%;
}

  .pr_16 {
    padding-right: var(--spacing-16);
}

  .min-h_30px {
    min-height: 30px;
}

  .max-h_4\.6rem {
    max-height: 4.6rem;
}

  .ml_1 {
    margin-left: var(--spacing-1);
}

  .h_4 {
    height: var(--sizes-4);
}

  .mr_5 {
    margin-right: var(--spacing-5);
}

  .min-w_28rem {
    min-width: 28rem;
}

  .min-w_16rem {
    min-width: 16rem;
}

  .max-w_34rem {
    max-width: 34rem;
}

  .mb_4 {
    margin-bottom: var(--spacing-4);
}

  .mt_-3 {
    margin-top: calc(var(--spacing-3) * -1);
}

  .ml_-8px {
    margin-left: -8px;
}

  .h_fit-content {
    height: fit-content;
}

  .mr_80px {
    margin-right: 80px;
}

  .h_12 {
    height: var(--sizes-12);
}

  .max-w_19\.5em {
    max-width: 19.5em;
}

  .max-h_80vh {
    max-height: 80vh;
}

  .h_auto {
    height: auto;
}

  .min-h_0 {
    min-height: var(--sizes-0);
}

  .w_1px {
    width: 1px;
}

  .ov-y_hidden {
    overflow-y: hidden;
}

  .pb_10 {
    padding-bottom: var(--spacing-10);
}

  .mb_6 {
    margin-bottom: var(--spacing-6);
}

  .w_588px {
    width: 588px;
}

  .max-w_900px {
    max-width: 900px;
}

  .h_calc\(100vh_-_var\(--chakra-space-8\)\) {
    height: calc(100vh - var(--chakra-space-8));
}

  .max-h_calc\(100vh_-_var\(--chakra-space-8\)\) {
    max-height: calc(100vh - var(--chakra-space-8));
}

  .mt_8 {
    margin-top: var(--spacing-8);
}

  .w_300px {
    width: 300px;
}

  .min-h_75px {
    min-height: 75px;
}

  .mt_10vh {
    margin-top: 10vh;
}

  .lc_1 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
}

  .ml_auto {
    margin-left: auto;
}

  .min-h_300px {
    min-height: 300px;
}

  .scr-mt_10vh {
    scroll-margin-top: 10vh;
}

  .left_50\% {
    left: 50%;
}

  .bottom_10 {
    bottom: var(--spacing-10);
}

  .mb_30px {
    margin-bottom: 30px;
}

  .w_5\% {
    width: 5%;
}

  .w_38\% {
    width: 38%;
}

  .w_17\% {
    width: 17%;
}

  .w_15\% {
    width: 15%;
}

  .w_19\% {
    width: 19%;
}

  .w_7\% {
    width: 7%;
}

  .w_2 {
    width: var(--sizes-2);
}

  .h_2 {
    height: var(--sizes-2);
}

  .mt_6px {
    margin-top: 6px;
}

  .ml_0\.5 {
    margin-left: var(--spacing-0\.5);
}

  .w_14px {
    width: 14px;
}

  .w_33\% {
    width: 33%;
}

  .mb_0 {
    margin-bottom: var(--spacing-0);
}

  .mt_6 {
    margin-top: var(--spacing-6);
}

  .mt_40\% {
    margin-top: 40%;
}

  .w_7px {
    width: 7px;
}

  .h_7px {
    height: 7px;
}

  .left_24px {
    left: 24px;
}

  .top_7px {
    top: 7px;
}

  .min-h_16 {
    min-height: var(--sizes-16);
}

  .w_600px {
    width: 600px;
}

  .top_100\% {
    top: 100%;
}

  .pb_4 {
    padding-bottom: var(--spacing-4);
}

  .max-w_28rem {
    max-width: 28rem;
}

  .mt_5vh {
    margin-top: 5vh;
}

  .max-w_8xl {
    max-width: var(--sizes-8xl);
}

  .w_145 {
    width: 145px;
}

  .h_50 {
    height: 50px;
}

  .max-w_550px {
    max-width: 550px;
}

  .mt_10 {
    margin-top: var(--spacing-10);
}

  .min-h_160px {
    min-height: 160px;
}

  .pl_32px {
    padding-left: 32px;
}

  .right_32px {
    right: 32px;
}

  .mt_-262px {
    margin-top: -262px;
}

  .pl_0 {
    padding-left: var(--spacing-0);
}

  .min-w_fit-content {
    min-width: fit-content;
}

  .w_100px {
    width: 100px;
}

  .w_80px {
    width: 80px;
}

  .w_13\.4rem {
    width: 13.4rem;
}

  .pt_6 {
    padding-top: var(--spacing-6);
}

  .pr_4 {
    padding-right: var(--spacing-4);
}

  .bd-b-c_whiteAlpha\.300 {
    border-bottom-color: var(--colors-white-alpha-300);
}

  .bd-t-c_whiteAlpha\.300 {
    border-top-color: var(--colors-white-alpha-300);
}

  .h_40px {
    height: 40px;
}

  .right_-15px {
    right: -15px;
}

  .right_-25px {
    right: -25px;
}

  .top_6 {
    top: var(--spacing-6);
}

  .mt_0 {
    margin-top: var(--spacing-0);
}

  .ml_2px {
    margin-left: 2px;
}

  .h_5 {
    height: var(--sizes-5);
}

  .w_13\% {
    width: 13%;
}

  .w_45\% {
    width: 45%;
}

  .w_14 {
    width: var(--sizes-14);
}

  .w_20 {
    width: var(--sizes-20);
}

  .w_16 {
    width: var(--sizes-16);
}

  .w_44 {
    width: var(--sizes-44);
}

  .max-w_70\% {
    max-width: 70%;
}

  .ml_4 {
    margin-left: var(--spacing-4);
}

  .pt_2\.5 {
    padding-top: var(--spacing-2\.5);
}

  .mr_16 {
    margin-right: var(--spacing-16);
}

  .ml_16 {
    margin-left: var(--spacing-16);
}

  .mb_1\.5 {
    margin-bottom: var(--spacing-1\.5);
}

  .mt_-6px {
    margin-top: -6px;
}

  .h_0 {
    height: var(--sizes-0);
}

  .max-w_300px {
    max-width: 300px;
}

  .w_calc_\(_100_\%_-_32px_\) {
    width: calc ( 100 % - 32px );
}

  .w_230px {
    width: 230px;
}

  .w_80\% {
    width: 80%;
}

  .max-w_80\% {
    max-width: 80%;
}

  .min-w_80\% {
    min-width: 80%;
}

  .w_75\% {
    width: 75%;
}

  .w_85px {
    width: 85px;
}

  .w_120px {
    width: 120px;
}

  .w_6 {
    width: var(--sizes-6);
}

  .min-h_40 {
    min-height: var(--sizes-40);
}

  .mt_5 {
    margin-top: var(--spacing-5);
}

  .w_6rem {
    width: 6rem;
}

  .w_36 {
    width: var(--sizes-36);
}

  .w_8 {
    width: var(--sizes-8);
}

  .pt_5 {
    padding-top: var(--spacing-5);
}

  .pb_1 {
    padding-bottom: var(--spacing-1);
}

  .pb_1px {
    padding-bottom: 1px;
}

  .mt_1px {
    margin-top: 1px;
}

  .pl_5 {
    padding-left: var(--spacing-5);
}

  .pt_4 {
    padding-top: var(--spacing-4);
}

  .pt_0 {
    padding-top: var(--spacing-0);
}

  .mr_8 {
    margin-right: var(--spacing-8);
}

  .w_24 {
    width: var(--sizes-24);
}

  .w_10 {
    width: var(--sizes-10);
}

  .w_40 {
    width: var(--sizes-40);
}

  .w_12 {
    width: var(--sizes-12);
}

  .w_20\% {
    width: 20%;
}

  .w_10\% {
    width: 10%;
}

  .max-h_370px {
    max-height: 370px;
}

  .min-w_8em {
    min-width: 8em;
}

  .max-w_16em {
    max-width: 16em;
}

  .mt_0\.35rem {
    margin-top: 0.35rem;
}

  .mb_2px {
    margin-bottom: 2px;
}

  .w_10px {
    width: 10px;
}

  .h_10px {
    height: 10px;
}

  .min-h_6 {
    min-height: var(--sizes-6);
}

  .w_56 {
    width: var(--sizes-56);
}

  .w_90\% {
    width: 90%;
}

  .min-w_16em {
    min-width: 16em;
}

  .h_7 {
    height: var(--sizes-7);
}

  .mr_3 {
    margin-right: var(--spacing-3);
}

  .h_0\.5 {
    height: var(--sizes-0\.5);
}

  .w_400 {
    width: 400px;
}

  .mt_10\% {
    margin-top: 10%;
}

  .mt_2vh {
    margin-top: 2vh;
}

  .bdr-bl_sm {
    border-bottom-left-radius: var(--radii-sm);
}

  .bdr-tl_sm {
    border-top-left-radius: var(--radii-sm);
}

  .bdr-br_lg {
    border-bottom-right-radius: var(--radii-lg);
}

  .bdr-tr_lg {
    border-top-right-radius: var(--radii-lg);
}

  .bd-l-w_1px {
    border-left-width: 1px;
}

  .bd-l-c_greys\.500 {
    border-left-color: var(--colors-greys-500);
}

  .top_-30 {
    top: -30px;
}

  .border-top-style_solid {
    border-top-style: solid;
}

  .bd-t-c_green\.400 {
    border-top-color: var(--colors-green-400);
}

  .border-left-style_dashed {
    border-left-style: dashed;
}

  .bd-l-c_green\.400 {
    border-left-color: var(--colors-green-400);
}

  .h_1 {
    height: var(--sizes-1);
}

  .h_20px {
    height: 20px;
}

  .w_40px {
    width: 40px;
}

  .h_32px {
    height: 32px;
}

  .w_35px {
    width: 35px;
}

  .w_140px {
    width: 140px;
}

  .mt_2\.5 {
    margin-top: var(--spacing-2\.5);
}

  .min-h_10 {
    min-height: var(--sizes-10);
}

  .pb_12 {
    padding-bottom: var(--spacing-12);
}

  .w_40\% {
    width: 40%;
}

  .w_60\% {
    width: 60%;
}

  .max-w_1000px {
    max-width: 1000px;
}

  .top_3 {
    top: var(--spacing-3);
}

  .h_250px {
    height: 250px;
}

  .w_55\% {
    width: 55%;
}

  .min-h_11rem {
    min-height: 11rem;
}

  .min-h_40px {
    min-height: 40px;
}

  .h_22px {
    height: 22px;
}

  .mt_24 {
    margin-top: var(--spacing-24);
}

  .max-w_180px {
    max-width: 180px;
}

  .min-w_100px {
    min-width: 100px;
}

  .bottom_4 {
    bottom: var(--spacing-4);
}

  .left_8 {
    left: var(--spacing-8);
}

  .h_20 {
    height: var(--sizes-20);
}

  .min-h_12rem {
    min-height: 12rem;
}

  .min-h_20rem {
    min-height: 20rem;
}

  .pr_3 {
    padding-right: var(--spacing-3);
}

  .h_1px {
    height: 1px;
}

  .w_fit-content {
    width: fit-content;
}

  .max-h_32px {
    max-height: 32px;
}

  .min-h_32px {
    min-height: 32px;
}

  .max-w_500px {
    max-width: 500px;
}

  .h_24px {
    height: 24px;
}

  .bd-b-c_graph\.primary\.300 {
    border-bottom-color: var(--colors-graph-primary-300);
}

  .bd-l-c_primary\.500 {
    border-left-color: var(--colors-primary-500);
}

  .bd-l-c_greys\.100 {
    border-left-color: var(--colors-greys-100);
}

  .bd-l-w_2 {
    border-left-width: 2px;
}

  .bd-l-w_0 {
    border-left-width: 0;
}

  .h_108px {
    height: 108px;
}

  .w_400px {
    width: 400px;
}

  .w_20px {
    width: 20px;
}

  .h_16px {
    height: 16px;
}

  .h_3 {
    height: var(--sizes-3);
}

  .w_272px {
    width: 272px;
}

  .h_209px {
    height: 209px;
}

  .bd-b-c_border\.light {
    border-bottom-color: var(--colors-border-light);
}

  .pr_5 {
    padding-right: var(--spacing-5);
}

  .h_15em {
    height: 15em;
}

  .min-h_150 {
    min-height: 150px;
}

  .max-w_200px {
    max-width: 200px;
}

  .h_15px {
    height: 15px;
}

  .bd-b-w_1px {
    border-bottom-width: 1px;
}

  .min-w_52rem {
    min-width: 52rem;
}

  .mt_24px {
    margin-top: 24px;
}

  .mt_0px {
    margin-top: 0px;
}

  .mb_3 {
    margin-bottom: var(--spacing-3);
}

  .mb_12 {
    margin-bottom: var(--spacing-12);
}

  .h_689px {
    height: 689px;
}

  .h_21px {
    height: 21px;
}

  .w_250px {
    width: 250px;
}

  .min-h_689px {
    min-height: 689px;
}

  .h_665px {
    height: 665px;
}

  .w_16\.5rem {
    width: 16.5rem;
}

  .w_5rem {
    width: 5rem;
}

  .h_60px {
    height: 60px;
}

  .pt_3 {
    padding-top: var(--spacing-3);
}

  .w_3 {
    width: var(--sizes-3);
}

  .h_18px {
    height: 18px;
}

  .h_12px {
    height: 12px;
}

  .min-h_512px {
    min-height: 512px;
}

  .pr_6 {
    padding-right: var(--spacing-6);
}

  .h_81px {
    height: 81px;
}

  .h_fit {
    height: var(--sizes-fit);
}

  .min-h_368px {
    min-height: 368px;
}

  .h_520px {
    height: 520px;
}

  .w_260px {
    width: 260px;
}

  .w_60 {
    width: var(--sizes-60);
}

  .min-h_384px {
    min-height: 384px;
}

  .h_514px {
    height: 514px;
}

  .min-h_298px {
    min-height: 298px;
}

  .w_518px {
    width: 518px;
}

  .h_70\% {
    height: 70%;
}

  .h_50\% {
    height: 50%;
}

  .h_40\% {
    height: 40%;
}

  .h_30\% {
    height: 30%;
}

  .w_1 {
    width: var(--sizes-1);
}

  .h_591px {
    height: 591px;
}

  .w_591px,.w_591 {
    width: 591px;
}

  .h_591 {
    height: 591px;
}

  .top_50\% {
    top: 50%;
}

  .min-h_500px {
    min-height: 500px;
}

  .min-h_32 {
    min-height: var(--sizes-32);
}

  .h_500px {
    height: 500px;
}

  .w_48 {
    width: var(--sizes-48);
}

  .h_32 {
    height: var(--sizes-32);
}

  .h_25px {
    height: 25px;
}

  .ml_3 {
    margin-left: var(--spacing-3);
}

  .w_150px {
    width: 150px;
}

  .w_160px {
    width: 160px;
}

  .ov-x_scroll {
    overflow-x: scroll;
}

  .max-w_150px {
    max-width: 150px;
}

  .mb_20 {
    margin-bottom: var(--spacing-20);
}

  .min-w_80 {
    min-width: var(--sizes-80);
}

  .right_8px {
    right: 8px;
}

  .min-w_8 {
    min-width: var(--sizes-8);
}

  .bdr-tr_md {
    border-top-right-radius: var(--radii-md);
}

  .bdr-br_md {
    border-bottom-right-radius: var(--radii-md);
}

  .w_3px {
    width: 3px;
}

  .h_calc\(100vh_-_180px\) {
    height: calc(100vh - 180px);
}

  .min-h_14 {
    min-height: var(--sizes-14);
}

  .min-w_10 {
    min-width: var(--sizes-10);
}

  .w_32px {
    width: 32px;
}

  .bottom_100\% {
    bottom: 100%;
}

  .min-w_26px {
    min-width: 26px;
}

  .w_84px {
    width: 84px;
}

  .h_3px {
    height: 3px;
}

  .w_116px {
    width: 116px;
}

  .min-w_30px {
    min-width: 30px;
}

  .w_64px {
    width: 64px;
}

  .ml_8px {
    margin-left: 8px;
}

  .lc_2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

  .w_initial {
    width: initial;
}

  .min-w_32em {
    min-width: 32em;
}

  .min-h_5em {
    min-height: 5em;
}

  .max-w_36em {
    max-width: 36em;
}

  .h_150px {
    height: 150px;
}

  .mr_auto {
    margin-right: auto;
}

  .h_110 {
    height: 110px;
}

  .h_50px {
    height: 50px;
}

  .bottom_0px {
    bottom: 0px;
}

  .mt_0\.5 {
    margin-top: var(--spacing-0\.5);
}

  .ml_-20px {
    margin-left: -20px;
}

  .bottom_4px {
    bottom: 4px;
}

  .mt_-16px {
    margin-top: -16px;
}

  .w_960px {
    width: 960px;
}

  .min-h_28px {
    min-height: 28px;
}

  .h_28px {
    height: 28px;
}

  .w_185px {
    width: 185px;
}

  .max-w_320px {
    max-width: 320px;
}

  .pl_8 {
    padding-left: var(--spacing-8);
}

  .bdr-bl_md {
    border-bottom-left-radius: var(--radii-md);
}

  .max-w_28em {
    max-width: 28em;
}

  .min-w_14em {
    min-width: 14em;
}

  .pr_12 {
    padding-right: var(--spacing-12);
}

  .mb_5 {
    margin-bottom: var(--spacing-5);
}

  .h_223px {
    height: 223px;
}

  .mr_6 {
    margin-right: var(--spacing-6);
}

  .mb_24px {
    margin-bottom: 24px;
}

  .mr_-1 {
    margin-right: calc(var(--spacing-1) * -1);
}

  .pb_5 {
    padding-bottom: var(--spacing-5);
}

  .mt_15vh {
    margin-top: 15vh;
}

  .mt_14vh {
    margin-top: 14vh;
}

  .mb_20vh {
    margin-bottom: 20vh;
}

  .w_0 {
    width: var(--sizes-0);
}

  .left_4px {
    left: 4px;
}

  .left_67\% {
    left: 67%;
}

  .left_33\% {
    left: 33%;
}

  .right_33\% {
    right: 33%;
}

  .bottom_-10px {
    bottom: -10px;
}

  .left_-3\.3px {
    left: -3.3px;
}

  .mt_-4px {
    margin-top: -4px;
}

  .w_1\.7px {
    width: 1.7px;
}

  .max-h_min\(800px\,_70vh\) {
    max-height: min(800px, 70vh);
}

  .h_300px {
    height: 300px;
}

  .w_4px {
    width: 4px;
}

  .right_19px {
    right: 19px;
}

  .top_calc\(50\%_-_3px\) {
    top: calc(50% - 3px);
}

  .w_6px {
    width: 6px;
}

  .h_6px {
    height: 6px;
}

  .pl_34px {
    padding-left: 34px;
}

  .h_55\% {
    height: 55%;
}

  .mb_6px {
    margin-bottom: 6px;
}

  .pt_32px {
    padding-top: 32px;
}

  .top_-4px {
    top: -4px;
}

  .bottom_-4px {
    bottom: -4px;
}

  .w_8px {
    width: 8px;
}

  .h_8px {
    height: 8px;
}

  .max-w_100px {
    max-width: 100px;
}

  .bdr-tl_12px {
    border-top-left-radius: 12px;
}

  .bdr-tr_12px {
    border-top-right-radius: 12px;
}

  .bdr-bl_12px {
    border-bottom-left-radius: 12px;
}

  .bdr-br_0 {
    border-bottom-right-radius: 0;
}

  .ov-y_visible {
    overflow-y: visible;
}

  .max-h_124px {
    max-height: 124px;
}

  .w_50\% {
    width: 50%;
}

  .min-h_118px {
    min-height: 118px;
}

  .pl_1 {
    padding-left: var(--spacing-1);
}

  .max-h_50vh {
    max-height: 50vh;
}

  .min-h_4 {
    min-height: var(--sizes-4);
}

  .min-h_47px {
    min-height: 47px;
}

  .min-w_22px {
    min-width: 22px;
}

  .mb_8px {
    margin-bottom: 8px;
}

  .max-h_400px {
    max-height: 400px;
}

  .max-h_350px {
    max-height: 350px;
}

  .max-h_550px {
    max-height: 550px;
}

  .max-h_450px {
    max-height: 450px;
}

  .min-h_20 {
    min-height: var(--sizes-20);
}

  .max-w_xl {
    max-width: var(--sizes-xl);
}

  .max-w_3xl {
    max-width: var(--sizes-3xl);
}

  .h_40 {
    height: var(--sizes-40);
}

  .min-h_182px {
    min-height: 182px;
}

  .bdr-tl_4 {
    border-top-left-radius: 4px;
}

  .bdr-bl_4 {
    border-bottom-left-radius: 4px;
}

  .top_40px {
    top: 40px;
}

  .max-h_330px {
    max-height: 330px;
}

  .left_0\% {
    left: 0%;
}

  .right_67\% {
    right: 67%;
}

  .right_0\% {
    right: 0%;
}

  .top_-8px {
    top: -8px;
}

  .mt_0\.3rem {
    margin-top: 0.3rem;
}

  .max-w_28 {
    max-width: var(--sizes-28);
}

  .left_0\.5 {
    left: var(--spacing-0\.5);
}

  .right_0\.5 {
    right: var(--spacing-0\.5);
}

  .w_4 {
    width: var(--sizes-4);
}

  .right_1 {
    right: var(--spacing-1);
}

  .top_1 {
    top: var(--spacing-1);
}

  .min-w_360px {
    min-width: 360px;
}

  .w_65\% {
    width: 65%;
}

  .w_35\% {
    width: 35%;
}

  .bd-l-w_4px {
    border-left-width: 4px;
}

  .bd-l-c_warning {
    border-left-color: var(--colors-warning);
}

  .bd-r-c_greys\.100 {
    border-right-color: var(--colors-greys-100);
}

  .bd-r-w_1px {
    border-right-width: 1px;
}

  .w_28px {
    width: 28px;
}

  .max-h_200px {
    max-height: 200px;
}

  .w_500 {
    width: 500px;
}

  .h_300 {
    height: 300px;
}

  .top_8 {
    top: var(--spacing-8);
}

  .w_50 {
    width: 50px;
}

  .h_18rem {
    height: 18rem;
}

  .min-w_100 {
    min-width: 100px;
}

  .right_50 {
    right: 50px;
}

  .min-h_8 {
    min-height: var(--sizes-8);
}

  .min-w_6 {
    min-width: var(--sizes-6);
}

  .max-h_min\(calc\(_100vh_-_264px_\)\,700px\) {
    max-height: min(calc( 100vh - 264px ),700px);
}

  .w_5em {
    width: 5em;
}

  .bdr-br_2xl {
    border-bottom-right-radius: var(--radii-2xl);
}

  .bdr-tr_2xl {
    border-top-right-radius: var(--radii-2xl);
}

  .max-w_35em {
    max-width: 35em;
}

  .w_50px {
    width: 50px;
}

  .top_9px {
    top: 9px;
}

  .h_4px {
    height: 4px;
}

  .h_2px {
    height: 2px;
}

  .h_1rem {
    height: 1rem;
}

  .mt_2px {
    margin-top: 2px;
}

  .bd-b-c_greys\.50 {
    border-bottom-color: var(--colors-greys-50);
}

  .right_1px {
    right: 1px;
}

  .top_1px {
    top: 1px;
}

  .w_163px {
    width: 163px;
}

  .mt_10px {
    margin-top: 10px;
}

  .h_16 {
    height: var(--sizes-16);
}

  .w_32\% {
    width: 32%;
}

  .w_48\% {
    width: 48%;
}

  .top_0\.5rem {
    top: 0.5rem;
}

  .min-h_450px {
    min-height: 450px;
}

  .mr_8px {
    margin-right: 8px;
}

  .mr_0px {
    margin-right: 0px;
}

  .bdr-bl_0px {
    border-bottom-left-radius: 0px;
}

  .bdr-br_0px {
    border-bottom-right-radius: 0px;
}

  .pb_6px {
    padding-bottom: 6px;
}

  .h_19px {
    height: 19px;
}

  .max-w_120px {
    max-width: 120px;
}

  .h_48 {
    height: var(--sizes-48);
}

  .bdr-tl_2xl {
    border-top-left-radius: var(--radii-2xl);
}

  .bdr-bl_0 {
    border-bottom-left-radius: 0;
}

  .pl_6 {
    padding-left: var(--spacing-6);
}

  .max-w_70px {
    max-width: 70px;
}

  .max-h_20rem {
    max-height: 20rem;
}

  .w_54px {
    width: 54px;
}

  .w_56px {
    width: 56px;
}

  .max-w_350px {
    max-width: 350px;
}

  .min-h_9 {
    min-height: var(--sizes-9);
}

  .right_15px {
    right: 15px;
}

  .w_24px {
    width: 24px;
}

  .h_14rem {
    height: 14rem;
}

  .max-w_600 {
    max-width: 600px;
}

  .max-h_900px {
    max-height: 900px;
}

  .max-h_700px {
    max-height: 700px;
}

  .h_31px {
    height: 31px;
}

  .max-w_245px {
    max-width: 245px;
}

  .w_5\.2em {
    width: 5.2em;
}

  .w_22em {
    width: 22em;
}

  .h_calc\(100\%_-_500px\) {
    height: calc(100% - 500px);
}

  .max-w_10em {
    max-width: 10em;
}

  .w_3\.5em {
    width: 3.5em;
}

  .right_4 {
    right: var(--spacing-4);
}

  .left_4 {
    left: var(--spacing-4);
}

  .left_10 {
    left: var(--spacing-10);
}

  .right_10 {
    right: var(--spacing-10);
}

  .pb_2px {
    padding-bottom: 2px;
}

  .min-w_155px {
    min-width: 155px;
}

  .max-w_40 {
    max-width: var(--sizes-40);
}

  .max-h_100\% {
    max-height: 100%;
}

  .lc_6 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
}

  .max-w_360px {
    max-width: 360px;
}

  .min-w_90px {
    min-width: 90px;
}

  .min-h_36px {
    min-height: 36px;
}

  .mt_33\% {
    margin-top: 33%;
}

  .w_max-content {
    width: max-content;
}

  .min-h_xl {
    min-height: var(--sizes-xl);
}

  .mt_12 {
    margin-top: var(--spacing-12);
}

  .ml_10 {
    margin-left: var(--spacing-10);
}

  .h_80\% {
    height: 80%;
}

  .bd-b-c_greys\.300 {
    border-bottom-color: var(--colors-greys-300);
}

  .ml_8 {
    margin-left: var(--spacing-8);
}

  .pb_50px {
    padding-bottom: 50px;
}

  .mt_40 {
    margin-top: var(--spacing-40);
}

  .w_80 {
    width: var(--sizes-80);
}

  .min-h_330px {
    min-height: 330px;
}

  .pt_20 {
    padding-top: var(--spacing-20);
}

  .max-h_24vh {
    max-height: 24vh;
}

  .ov-y_inherit {
    overflow-y: inherit;
}

  .left_2\.5 {
    left: var(--spacing-2\.5);
}

  .right_2\.5 {
    right: var(--spacing-2\.5);
}

  .top_2px {
    top: 2px;
}

  .ml_5 {
    margin-left: var(--spacing-5);
}

  .w_70px {
    width: 70px;
}

  .w_30px {
    width: 30px;
}

  .min-w_auto {
    min-width: auto;
}

  .max-w_12 {
    max-width: var(--sizes-12);
}

  .max-h_12 {
    max-height: var(--sizes-12);
}

  .w_28 {
    width: var(--sizes-28);
}

  .pl_2px {
    padding-left: 2px;
}

  .min-h_auto {
    min-height: auto;
}

  .max-w_20rem {
    max-width: 20rem;
}

  .w_2rem {
    width: 2rem;
}

  .w_8rem {
    width: 8rem;
}

  .h_13px {
    height: 13px;
}

  .max-w_12em {
    max-width: 12em;
}

  .bdr-tr_0 {
    border-top-right-radius: 0;
}

  .bdr-tl_0 {
    border-top-left-radius: 0;
}

  .max-h_96 {
    max-height: var(--sizes-96);
}

  .w_48px {
    width: 48px;
}

  .w_4\.2em {
    width: 4.2em;
}

  .min-h_4px {
    min-height: 4px;
}

  .ml_26px {
    margin-left: 26px;
}

  .max-w_50px {
    max-width: 50px;
}

  .min-h_120px {
    min-height: 120px;
}

  .h_34px {
    height: 34px;
}

  .mt_4px {
    margin-top: 4px;
}

  .h_200 {
    height: 200px;
}

  .w_244px {
    width: 244px;
}

  .w_350px {
    width: 350px;
}

  .mb_-4 {
    margin-bottom: calc(var(--spacing-4) * -1);
}

  .max-w_xs {
    max-width: var(--sizes-xs);
}

  .h_2\.5 {
    height: var(--sizes-2\.5);
}

  .min-h_250 {
    min-height: 250px;
}

  .h_60 {
    height: var(--sizes-60);
}

  .w_25\% {
    width: 25%;
}

  .mt_0\.2em {
    margin-top: 0.2em;
}

  .w_300 {
    width: 300px;
}

  .h_100 {
    height: 100px;
}

  .w_32 {
    width: var(--sizes-32);
}

  .max-w_56 {
    max-width: var(--sizes-56);
}

  .min-h_440px {
    min-height: 440px;
}

  .mt_45\% {
    margin-top: 45%;
}

  .h_400px {
    height: 400px;
}

  .h_30px {
    height: 30px;
}

  .max-h_66vh {
    max-height: 66vh;
}

  .mt_50px {
    margin-top: 50px;
}

  .mr_10 {
    margin-right: var(--spacing-10);
}

  .h_stretch {
    height: stretch;
}

  .h_127px {
    height: 127px;
}

  .max-h_300px {
    max-height: 300px;
}

  .mt_-30px {
    margin-top: -30px;
}

  .min-h_20em {
    min-height: 20em;
}

  .max-w_273px {
    max-width: 273px;
}

  .bd-t-c_inherit {
    border-top-color: inherit;
}

  .max-h_40vh {
    max-height: 40vh;
}

  .w_184px {
    width: 184px;
}

  .bd-b-w_1 {
    border-bottom-width: 1px;
}

  .border-bottom-style_solid {
    border-bottom-style: solid;
}

  .bd-b-w_2 {
    border-bottom-width: 2px;
}

  .bd-t-c_border\.light {
    border-top-color: var(--colors-border-light);
}

  .bdr-tr_0\.25rem {
    border-top-right-radius: 0.25rem;
}

  .bdr-br_0\.25rem {
    border-bottom-right-radius: 0.25rem;
}

  .pr_0\.4em {
    padding-right: 0.4em;
}

  .max-w_1px {
    max-width: 1px;
}

  .ml_0\.1em {
    margin-left: 0.1em;
}

  .max-h_60vh {
    max-height: 60vh;
}

  .w_40ch {
    width: 40ch;
}

  .max-h_750px {
    max-height: 750px;
}

  .max-w_inherit {
    max-width: inherit;
}

  .min-w_1\.25rem {
    min-width: 1.25rem;
}

  .max-w_85px {
    max-width: 85px;
}

  .min-h_100px {
    min-height: 100px;
}

  .h_70px {
    height: 70px;
}

  .before\:bg_linear-gradient\(rgba\(239\,_58\,_66\,_0\.5\)_0\%\,_rgba\(255\,_178\,_32\,_0\.5\)_49\%\,_rgba\(255\,_145\,_179\,_0\.5\)_100\%\)\;::before {
    background: linear-gradient(rgba(239, 58, 66, 0.5) 0%, rgba(255, 178, 32, 0.5) 49%, rgba(255, 145, 179, 0.5) 100%);;
}

  .before\:p_1px::before {
    padding: 1px;
}

  .before\:inset_-2px::before {
    inset: -2px;
}

  .before\:bg_gradients\.aiStrokeText::before {
    background: var(--colors-gradients-ai-stroke-text);
}

  .checked\:bg_greys\.100:is(:checked, [data-checked], [aria-checked=true], [data-state="checked"]) {
    background: var(--colors-greys-100);
}

  .checked\:bg_primary\.500:is(:checked, [data-checked], [aria-checked=true], [data-state="checked"]) {
    background: var(--colors-primary-500);
}

  .before\:msk_linear-gradient\(rgb\(255\,_255\,_255\)_0px\,_rgb\(255\,_255\,_255\)_0px\)_content-box_exclude\,_linear-gradient\(rgb\(255\,_255\,_255\)_0px\,_rgb\(255\,_255\,_255\)_0px\)::before {
    mask: linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px) content-box exclude, linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
    -webkit-mask: linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px) content-box exclude, linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
}

  .before\:bdr_inherit::before {
    border-radius: inherit;
}

  .\[\&_\.recharts-sector\]\:trs_opacity_150ms_ease .recharts-sector {
    transition: opacity 150ms ease;
}

  .\[\&_\.recharts-sector\]\:ring_none .recharts-sector,.\[\&_\.recharts-wrapper\]\:ring_none .recharts-wrapper,.\[\&_\.recharts-surface\]\:ring_none .recharts-surface {
    outline: var(--borders-none);
}

  .after\:border-l_6px_solid_transparent::after {
    border-left: 6px solid transparent;
}

  .after\:border-r_6px_solid_transparent::after {
    border-right: 6px solid transparent;
}

  .after\:border-t_6px_solid_white::after {
    border-top: 6px solid white;
}

  .\[\&\:not\(\:last-child\)\]\:border-b_none:not(:last-child) {
    border-bottom: var(--borders-none);
}

  .last\:border-b_0px:last-child {
    border-bottom: solid 0px;
}

  .last\:border-r_none:last-child {
    border-right: var(--borders-none);
}

  .checked\:bd-c_primary\.500:is(:checked, [data-checked], [aria-checked=true], [data-state="checked"]) {
    border-color: var(--colors-primary-500);
}

  .before\:content_\'\'::before {
    content: '';
}

  .before\:position_absolute::before {
    position: absolute;
}

  .before\:z_-1::before {
    z-index: -1;
}

  .\[\&_\.recharts-sector\]\:cursor_pointer .recharts-sector {
    cursor: pointer;
}

  .\[\&_\.recharts-sector\]\:us_none .recharts-sector {
    -webkit-user-select: none;
    user-select: none;
}

  .\[\&_\.recharts-sector\]\:-webkit-tap-highlight-color_transparent .recharts-sector,.\[\&_\.recharts-wrapper\]\:-webkit-tap-highlight-color_transparent .recharts-wrapper {
    -webkit-tap-highlight-color: transparent;
}

  .\[\&_\.recharts-wrapper\]\:us_none .recharts-wrapper {
    -webkit-user-select: none;
    user-select: none;
}

  .\[\&_\.recharts-surface\]\:-webkit-tap-highlight-color_transparent .recharts-surface {
    -webkit-tap-highlight-color: transparent;
}

  .\[\&_\.recharts-surface\]\:us_none .recharts-surface {
    -webkit-user-select: none;
    user-select: none;
}

  .\[\&\.table-hovering_\.recharts-sector\]\:op_0\.6.table-hovering .recharts-sector {
    opacity: 0.6;
}

  .\[\&\.table-hovering_\.recharts-sector\.is-hovered\]\:op_1.table-hovering .recharts-sector.is-hovered {
    opacity: 1;
}

  .after\:content_\"\"::after {
    content: "";
}

  .after\:position_absolute::after {
    position: absolute;
}

  .after\:trf_translateX\(-50\%\)::after {
    transform: translateX(-50%);
}

  .first\:bdr-t_5px:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
}

  .last\:bdr-b_5px:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
}

  .checked\:color_white:is(:checked, [data-checked], [aria-checked=true], [data-state="checked"]) {
    color: var(--colors-white);
}

  .disabled\:op_0\.5:is(:disabled, [disabled], [data-disabled], [aria-disabled=true]) {
    opacity: 0.5;
}

  .after\:top_100\%::after {
    top: 100%;
}

  .after\:left_50\%::after {
    left: 50%;
}

  .focusWithin\:bd-c_primary\.500:focus-within {
    border-color: var(--colors-primary-500);
}

  .focusWithin\:bd-c_outlineColor:focus-within {
    border-color: var(--colors-outline-color);
}

  .focusWithin\:bx-sh_extended:focus-within {
    box-shadow: var(--shadows-extended);
}

  .focusWithin\:bx-sh_0_0_0_1px_var\(--chakra-colors-primary-500\):focus-within {
    box-shadow: 0 0 0 1px var(--chakra-colors-primary-500);
}

  .focusWithin\:bx-sh_outline:focus-within {
    box-shadow: var(--shadows-outline);
}

  .focus\:bg_greys\.100:is(:focus, [data-focus]) {
    background: var(--colors-greys-100);
}

  .focus\:bg_blackAlpha\.100:is(:focus, [data-focus]) {
    background: var(--colors-black-alpha-100);
}

  .focus\:ring_none:is(:focus, [data-focus]) {
    outline: var(--borders-none);
}

  .focusVisible\:border-r_none:is(:focus-visible, [data-focus-visible]) {
    border-right: var(--borders-none);
}

  .\[\&_\.recharts-sector\:focus\:not\(\:focus-visible\)\]\:ring_none .recharts-sector:focus:not(:focus-visible),.\[\&_\.recharts-surface\:focus\:not\(\:focus-visible\)\]\:ring_none .recharts-surface:focus:not(:focus-visible),.\[\&_\.recharts-wrapper\:focus\:not\(\:focus-visible\)\]\:ring_none .recharts-wrapper:focus:not(:focus-visible) {
    outline: var(--borders-none);
}

  .focus\:bd-c_primary\.600:is(:focus, [data-focus]) {
    border-color: var(--colors-primary-600);
}

  .focus\:bd-c_danger:is(:focus, [data-focus]) {
    border-color: danger;
}

  .focus\:bd-c_initial:is(:focus, [data-focus]) {
    border-color: initial;
}

  .focusVisible\:bd-c_danger:is(:focus-visible, [data-focus-visible]) {
    border-color: danger;
}

  .focusVisible\:bd-c_initial:is(:focus-visible, [data-focus-visible]) {
    border-color: initial;
}

  .focus\:bg-c_none:is(:focus, [data-focus]) {
    background-color: none;
}

  .focus\:bg-c_primary\.600:is(:focus, [data-focus]) {
    background-color: var(--colors-primary-600);
}

  .focus\:bx-sh_none:is(:focus, [data-focus]) {
    box-shadow: none;
}

  .focus\:bx-sh_0_0_0_3px_\#004EFC33:is(:focus, [data-focus]) {
    box-shadow: 0 0 0 3px #004EFC33;
}

  .focus\:bx-sh_outline:is(:focus, [data-focus]) {
    box-shadow: var(--shadows-outline);
}

  .group:is(:focus, [data-focus]) .groupFocus\:op_1 {
    opacity: 1;
}

  .focus\:z_1:is(:focus, [data-focus]) {
    z-index: 1;
}

  .hover\:bg_greys\.100:is(:hover, [data-hover]) {
    background: var(--colors-greys-100);
}

  .hover\:bg_none:is(:hover, [data-hover]) {
    background: none;
}

  .hover\:bg_greys\.200:is(:hover, [data-hover]) {
    background: var(--colors-greys-200);
}

  .hover\:bg_greys\.50:is(:hover, [data-hover]) {
    background: var(--colors-greys-50);
}

  .hover\:bg_gradients\.aiGradientWeak:is(:hover, [data-hover]) {
    background: var(--colors-gradients-ai-gradient-weak);
}

  .hover\:bg_unset:is(:hover, [data-hover]) {
    background: unset;
}

  .hover\:bg_transparent:is(:hover, [data-hover]) {
    background: var(--colors-transparent);
}

  .hover\:bg_segments\.primary\.600:is(:hover, [data-hover]) {
    background: var(--colors-segments-primary-600);
}

  .hover\:bg_blackAlpha\.100:is(:hover, [data-hover]) {
    background: var(--colors-black-alpha-100);
}

  .hover\:bg_primary\.50:is(:hover, [data-hover]) {
    background: primary.50;
}

  .hover\:bg_greys\.white:is(:hover, [data-hover]) {
    background: var(--colors-greys-white);
}

  .hover\:bg_gray\.200:is(:hover, [data-hover]) {
    background: var(--colors-gray-200);
}

  .group:is(:hover, [data-hover]) .groupHover\:td_underline,.hover\:td_underline:is(:hover, [data-hover]) {
    text-decoration: underline;
}

  .\[\&\:hover\]\:td_none:hover,.hover\:td_none:is(:hover, [data-hover]) {
    text-decoration: none;
}

  .hover\:bd-c_primary\.500:is(:hover, [data-hover]) {
    border-color: var(--colors-primary-500);
}

  .hover\:bd-c_inherit:is(:hover, [data-hover]) {
    border-color: inherit;
}

  .hover\:bd-c_greys\.200:is(:hover, [data-hover]) {
    border-color: var(--colors-greys-200);
}

  .hover\:bd-c_greys\.100:is(:hover, [data-hover]) {
    border-color: var(--colors-greys-100);
}

  .hover\:bd-c_primary\.400:is(:hover, [data-hover]) {
    border-color: primary.400;
}

  .group:is(:hover, [data-hover]) .groupHover\:vis_hidden {
    visibility: hidden;
}

  .group:is(:hover, [data-hover]) .groupHover\:d_block {
    display: block;
}

  .hover\:color_primary\.500:is(:hover, [data-hover]) {
    color: var(--colors-primary-500);
}

  .hover\:trf_scale\(1\.7\)_translate\(2px\,_-2px\):is(:hover, [data-hover]) {
    transform: scale(1.7) translate(2px, -2px);
}

  .hover\:anim-dly_700ms:is(:hover, [data-hover]) {
    animation-delay: 700ms;
}

  .hover\:z_1:is(:hover, [data-hover]) {
    z-index: 1;
}

  .hover\:bg-c_none:is(:hover, [data-hover]) {
    background-color: none;
}

  .hover\:bg-c_primary\.600:is(:hover, [data-hover]) {
    background-color: var(--colors-primary-600);
}

  .hover\:trf_scale\(1\.1\):is(:hover, [data-hover]) {
    transform: scale(1.1);
}

  .hover\:trf_scale\(1\.2\):is(:hover, [data-hover]) {
    transform: scale(1.2);
}

  .\[\&_\.recharts-pie\:hover_\.recharts-sector\]\:op_0\.6 .recharts-pie:hover .recharts-sector {
    opacity: 0.6;
}

  .\[\&_\.recharts-pie\:hover_\.recharts-sector\:hover\]\:op_1 .recharts-pie:hover .recharts-sector:hover {
    opacity: 1;
}

  .hover\:bg-c_transparent:is(:hover, [data-hover]) {
    background-color: var(--colors-transparent);
}

  .hover\:bg-c_greys\.100:is(:hover, [data-hover]) {
    background-color: var(--colors-greys-100);
}

  .group:is(:hover, [data-hover]) .groupHover\:op_1 {
    opacity: 1;
}

  .\[\&_text\:hover\]\:cursor_pointer text:hover {
    cursor: pointer;
}

  .\[\&_text\:hover\]\:filter_brightness\(85\%\) text:hover {
    filter: brightness(85%);
}

  .hover\:cursor_pointer:is(:hover, [data-hover]) {
    cursor: pointer;
}

  .hover\:trf_scale\(1\.02\):is(:hover, [data-hover]) {
    transform: scale(1.02);
}

  .hover\:bg-c_greys\.50:is(:hover, [data-hover]) {
    background-color: var(--colors-greys-50);
}

  .hover\:bg-c_white:is(:hover, [data-hover]) {
    background-color: var(--colors-white);
}

  .hover\:bg-c_rgba\(0\,_0\,_0\,_0\.2\)\;:is(:hover, [data-hover]) {
    background-color: rgba(0, 0, 0, 0.2);;
}

  .group:is(:hover, [data-hover]) .groupHover\:pointer-events_auto {
    pointer-events: auto;
}

  .group:is(:hover, [data-hover]) .groupHover\:trs-dly_0\.3s {
    transition-delay: 0.3s;
}

  .hover\:bg-c_\#25748b:is(:hover, [data-hover]) {
    background-color: #25748b;
}

  .hover\:bg-c_\#D5EAF6:is(:hover, [data-hover]) {
    background-color: #D5EAF6;
}

  .hover\:color_primary\.400:is(:hover, [data-hover]) {
    color: primary.400;
}

  .hover\:color_mediumseagreen:is(:hover, [data-hover]) {
    color: mediumseagreen;
}

  .hover\:color_grey:is(:hover, [data-hover]) {
    color: grey;
}

  .hover\:color_orangered:is(:hover, [data-hover]) {
    color: orangered;
}

  .hover\:bg-c_greys\.white:is(:hover, [data-hover]) {
    background-color: var(--colors-greys-white);
}

  .hover\:bx-sh_inset_0px_0px_5px_20px_rgba\(0\,0\,0\,\.08\):is(:hover, [data-hover]) {
    box-shadow: inset 0px 0px 5px 20px rgba(0,0,0,.08);
}

  .active\:bg_greys\.300:is(:active, [data-active]) {
    background: var(--colors-greys-300);
}

  .active\:bg_transparent:is(:active, [data-active]) {
    background: var(--colors-transparent);
}

  .active\:bg_greys\.100:is(:active, [data-active]) {
    background: var(--colors-greys-100);
}

  .active\:bg_greys\.200:is(:active, [data-active]) {
    background: var(--colors-greys-200);
}

  .active\:bg-c_greys\.200:is(:active, [data-active]) {
    background-color: var(--colors-greys-200);
}

  .active\:bx-sh_inset_0px_0px_5px_20px_rgba\(0\,0\,0\,\.12\):is(:active, [data-active]) {
    box-shadow: inset 0px 0px 5px 20px rgba(0,0,0,.12);
}

  .active\:bg-c_transparent:is(:active, [data-active]) {
    background-color: var(--colors-transparent);
}

  @media screen and (min-width: 30rem) {
    .sm\:p_4 {
      padding: var(--spacing-4);
}
    .sm\:px_4 {
      padding-inline: var(--spacing-4);
}
    .sm\:bg-grad_linear\(204\.75deg\,_rgba\(255\,_65\,_5\,_0\)_17\.4\%\,_rgba\(255\,_218\,_121\,_0\.0145\)_46\.85\%\,_rgba\(58\,_243\,_255\,_0\.03\)_59\.14\%\,_rgba\(56\,_207\,_255\,_0\)_82\.39\%\) {
      background-image: linear(204.75deg, rgba(255, 65, 5, 0) 17.4%, rgba(255, 218, 121, 0.0145) 46.85%, rgba(58, 243, 255, 0.03) 59.14%, rgba(56, 207, 255, 0) 82.39%);
}
    .sm\:flex-d_row {
      flex-direction: row;
}
    .sm\:grid-tc_1fr {
      grid-template-columns: 1fr;
}
    .sm\:flex-d_column {
      flex-direction: column;
}
    .sm\:pl_12 {
      padding-left: var(--spacing-12);
}
    .sm\:w_100\% {
      width: 100%;
}
}

  @media screen and (min-width: 48rem) {
    .md\:border-r_1px_solid_var\(--chakra-colors-greys-100\) {
      border-right: 1px solid var(--chakra-colors-greys-100);
}
    .md\:border-r_none {
      border-right: var(--borders-none);
}
    .md\:px_8 {
      padding-inline: var(--spacing-8);
}
    .md\:gap_4 {
      gap: var(--spacing-4);
}
    .md\:px_6 {
      padding-inline: var(--spacing-6);
}
    .md\:grid-c_span_2 {
      grid-column: span 2;
}
    .md\:grid-r_span_3 {
      grid-row: span 3;
}
    .md\:grid-r_span_2 {
      grid-row: span 2;
}
    .md\:grid-c_span_3 {
      grid-column: span 3;
}
    .md\:px_4 {
      padding-inline: var(--spacing-4);
}
    .md\:py_2 {
      padding-block: var(--spacing-2);
}
    .md\:fs_2xl {
      font-size: var(--font-sizes-2xl);
}
    .md\:flex-d_row {
      flex-direction: row;
}
    .md\:ai_center {
      align-items: center;
}
    .md\:items_flex-end {
      align-items: flex-end;
}
    .md\:flex-d_row-reverse {
      flex-direction: row-reverse;
}
    .md\:bg-grad_linear\(175\.78deg\,_\#FFFFFF_3\.66\%\,_\#FAFAFA_96\.79\%\) {
      background-image: linear(175.78deg, #FFFFFF 3.66%, #FAFAFA 96.79%);
}
    .md\:d_inline-flex {
      display: inline-flex;
}
    .md\:d_block {
      display: block;
}
    .md\:fs_sm {
      font-size: var(--font-sizes-sm);
}
    .md\:flex-d_column {
      flex-direction: column;
}
    .md\:pr_8 {
      padding-right: var(--spacing-8);
}
    .md\:pr_0 {
      padding-right: var(--spacing-0);
}
    .md\:pb_0 {
      padding-bottom: var(--spacing-0);
}
    .md\:max-h_430px {
      max-height: 430px;
}
    .md\:w_48em {
      width: 48em;
}
    .md\:pl_12 {
      padding-left: var(--spacing-12);
}
    .md\:w_50\% {
      width: 50%;
}
    .md\:max-h_68vh {
      max-height: 68vh;
}
    .md\:max-h_77vh {
      max-height: 77vh;
}
}

  @media screen and (min-width: 62rem) {
    .lg\:gap_8 {
      gap: var(--spacing-8);
}
    .lg\:px_8 {
      padding-inline: var(--spacing-8);
}
    .lg\:px_2 {
      padding-inline: var(--spacing-2);
}
    .lg\:grid-c_span_1 {
      grid-column: span 1;
}
    .lg\:my_1 {
      margin-block: var(--spacing-1);
}
    .lg\:fs_3xl {
      font-size: var(--font-sizes-3xl);
}
    .lg\:fs_lg {
      font-size: var(--font-sizes-lg);
}
    .lg\:d_block {
      display: block;
}
    .lg\:flex-d_row {
      flex-direction: row;
}
    .lg\:ai_center {
      align-items: center;
}
    .lg\:jc_flex-end {
      justify-content: flex-end;
}
    .lg\:vis_hidden {
      visibility: hidden;
}
    .lg\:grid-tc_repeat\(auto-fill\,_300px\) {
      grid-template-columns: repeat(auto-fill, 300px);
}
    .lg\:pl_12 {
      padding-left: var(--spacing-12);
}
    .lg\:mt_5 {
      margin-top: var(--spacing-5);
}
}

  @media screen and (min-width: 80rem) {
    .xl\:grid-c_span_2 {
      grid-column: span 2;
}
    .xl\:grid-r_span_3 {
      grid-row: span 3;
}
    .xl\:grid-c_span_1 {
      grid-column: span 1;
}
    .xl\:grid-r_span_1 {
      grid-row: span 1;
}
    .xl\:grid-r_span_2 {
      grid-row: span 2;
}
    .xl\:flex-d_row {
      flex-direction: row;
}
    .xl\:ac_stretch {
      align-content: stretch;
}
    .xl\:d_block {
      display: block;
}
    .xl\:fs_xl {
      font-size: var(--font-sizes-xl);
}
    .xl\:pl_8 {
      padding-left: var(--spacing-8);
}
    .xl\:w_1px {
      width: 1px;
}
    .xl\:h_auto {
      height: auto;
}
}

  @media screen and (min-width: 118.75rem) {
    .\33xl\:d_block {
      display: block;
}
}
}